// apps/web/src/views/app-views/quiz/appearance/index.js

import React, {
    useCallback,
    useEffect,
    useState,
} from 'react'

import {
    Alert,
    Button,
    Card,
    Col,
    Divider,
    Form,
    Input,
    InputNumber,
    message,
    Row,
    Select,
    Space,
    Spin,
    Switch,
    Tabs,
    Typography,
    Upload,
} from 'antd'

import {
    DeleteOutlined,
    ReloadOutlined,
    SaveOutlined,
    UploadOutlined,
} from '@ant-design/icons'

import {
    useSearchParams,
} from 'react-router-dom'

import fetch from 'auth/FetchInterceptor'

import UploadService from 'services/UploadService'

import {
    env,
} from 'configs/EnvironmentConfig'

const {
    Title,
    Text,
} = Typography

const API_BASE_URL =
    env.API_ENDPOINT_URL

// ======================================================
// HELPERS
// ======================================================

const getMediaUrl = (
    value,
) => {
    if (!value) {
        return ''
    }

    if (
        value.startsWith(
            'http://',
        ) ||
        value.startsWith(
            'https://',
        ) ||
        value.startsWith(
            'data:',
        ) ||
        value.startsWith(
            'blob:',
        )
    ) {
        return value
    }

    return `${API_BASE_URL}${value}`
}

const getUploadUrl = (
    response,
) => {
    return (
        response?.data?.url ||
        response?.data?.path ||
        response?.url ||
        response?.path ||
        ''
    )
}

const getErrorMessage = (
    error,
    fallback,
) => {
    const value =
        error?.response
            ?.data
            ?.message ||
        error?.message ||
        fallback

    if (
        Array.isArray(
            value,
        )
    ) {
        return value.join(
            ', ',
        )
    }

    return value
}

const getPositionStyle = (
    position,
) => {
    switch (
    position
    ) {
        case 'LEFT':
            return {
                alignItems:
                    'flex-start',
            }

        case 'RIGHT':
            return {
                alignItems:
                    'flex-end',
            }

        case 'TOP':
            return {
                justifyContent:
                    'flex-start',
            }

        case 'BOTTOM':
            return {
                justifyContent:
                    'flex-end',
            }

        default:
            return {
                alignItems:
                    'center',

                justifyContent:
                    'center',
            }
    }
}

// ======================================================
// CAMPO DE COR
// ======================================================

const ColorField = ({
    label,
    name,
}) => {
    const form =
        Form.useFormInstance()

    const value =
        Form.useWatch(
            name,
            form,
        )

    return (
        <Form.Item
            label={
                label
            }
        >
            <Space.Compact
                style={{
                    width:
                        '100%',
                }}
            >
                <input
                    type="color"
                    value={
                        (
                            value &&
                            String(
                                value,
                            ).startsWith(
                                '#',
                            )
                        )
                            ? value
                            : '#000000'
                    }
                    onChange={(
                        event,
                    ) => {
                        form.setFieldValue(
                            name,
                            event.target.value,
                        )
                    }}
                    style={{
                        width:
                            50,

                        minWidth:
                            50,

                        height:
                            32,

                        padding:
                            2,

                        border:
                            '1px solid #d9d9d9',

                        cursor:
                            'pointer',
                    }}
                />

                <Form.Item
                    name={
                        name
                    }
                    noStyle
                >
                    <Input />
                </Form.Item>
            </Space.Compact>
        </Form.Item>
    )
}



// ======================================================
// UPLOAD DE MÍDIA
// ======================================================

const MediaField = ({
    form,
    label,
    fieldName,
    type = 'image',
    uploading,
    onUpload,
}) => {
    const currentUrl =
        Form.useWatch(
            fieldName,
            form,
        )

    const isVideo =
        type ===
        'video'

    return (
        <Form.Item
            label={
                label
            }
        >
            <Space
                direction="vertical"
                style={{
                    width:
                        '100%',
                }}
            >
                <Form.Item
                    name={
                        fieldName
                    }
                    noStyle
                >
                    <Input
                        readOnly
                        placeholder={
                            isVideo
                                ? 'Nenhum vídeo enviado'
                                : 'Nenhuma imagem enviada'
                        }
                    />
                </Form.Item>

                <Space
                    wrap
                >
                    <Upload
                        accept={
                            isVideo
                                ? 'video/*,.mp4,.webm,.ogg'
                                : 'image/*,.png,.jpg,.jpeg,.webp'
                        }
                        showUploadList={
                            false
                        }
                        beforeUpload={(
                            file,
                        ) => {
                            onUpload(
                                fieldName,
                                file,
                                type,
                            )

                            return false
                        }}
                    >
                        <Button
                            icon={
                                <UploadOutlined />
                            }
                            loading={
                                uploading ===
                                fieldName
                            }
                        >
                            {currentUrl
                                ? 'Trocar'
                                : 'Enviar'}
                        </Button>
                    </Upload>

                    <Button
                        danger
                        icon={
                            <DeleteOutlined />
                        }
                        disabled={
                            !currentUrl
                        }
                        onClick={() => {
                            form.setFieldValue(
                                fieldName,
                                '',
                            )
                        }}
                    >
                        Remover
                    </Button>
                </Space>

                {currentUrl && (
                    isVideo
                        ? (
                            <video
                                src={
                                    getMediaUrl(
                                        currentUrl,
                                    )
                                }
                                controls
                                muted
                                style={{
                                    width:
                                        '100%',

                                    maxHeight:
                                        180,

                                    objectFit:
                                        'contain',

                                    background:
                                        '#111',

                                    borderRadius:
                                        8,
                                }}
                            />
                        )
                        : (
                            <img
                                src={
                                    getMediaUrl(
                                        currentUrl,
                                    )
                                }
                                alt=""
                                style={{
                                    width:
                                        '100%',

                                    maxHeight:
                                        180,

                                    objectFit:
                                        'contain',

                                    background:
                                        '#f5f5f5',

                                    borderRadius:
                                        8,
                                }}
                            />
                        )
                )}
            </Space>
        </Form.Item>
    )
}

// ======================================================
// PREVIEW
// ======================================================

const QuizPreview = ({
    values,
    previewScreen,
}) => {
    const orientation =
        values.orientation ||
        'VERTICAL'

    const isHorizontal =
        orientation ===
        'HORIZONTAL'

    const screenWidth =
        Number(
            values.screenWidth,
        ) ||
        (
            isHorizontal
                ? 1920
                : 1080
        )

    const screenHeight =
        Number(
            values.screenHeight,
        ) ||
        (
            isHorizontal
                ? 1080
                : 1920
        )

    const previewWidth =
        isHorizontal
            ? 640
            : 360

    const previewHeight =
        previewWidth *
        (
            screenHeight /
            screenWidth
        )

    const scale =
        previewWidth /
        screenWidth

    // ======================================================
    // MEDIDAS REAIS
    // ======================================================
    //
    // O conteúdo do preview agora é desenhado exatamente
    // na resolução configurada:
    //
    // Vertical:   1080 x 1920
    // Horizontal: 1920 x 1080
    //
    // Depois o palco inteiro é reduzido visualmente usando
    // transform: scale(...).
    //
    // Portanto NÃO devemos mais reduzir individualmente
    // fontes, larguras, paddings, offsets etc.
    // ======================================================

    const px = (
        value,
        fallback,
    ) => {
        return Number(
            value ??
            fallback,
        )
    }

    // ==================================================
    // BACKGROUND
    // ==================================================

    let backgroundColor =
        '#000000'

    let backgroundUrl =
        ''

    let backgroundVideoUrl =
        ''

    let mediaType =
        'IMAGE'

    let overlayEnabled =
        false

    let overlayColor =
        'transparent'

    if (
        previewScreen ===
        'START'
    ) {
        backgroundColor =
            values.startScreenBackgroundColor ||
            '#000000'

        backgroundUrl =
            values.startScreenBackgroundUrl

        backgroundVideoUrl =
            values.startScreenBackgroundVideoUrl

        mediaType =
            values.startScreenMediaType ||
            'IMAGE'

        overlayEnabled =
            values.startScreenOverlayEnabled

        overlayColor =
            values.startScreenOverlayColor ||
            'rgba(0,0,0,0.25)'
    }

    if (
        previewScreen ===
        'QUESTION'
    ) {
        backgroundColor =
            values.questionBackgroundColor ||
            '#000000'

        backgroundUrl =
            values.questionBackgroundUrl

        backgroundVideoUrl =
            values.questionBackgroundVideoUrl

        mediaType =
            values.questionMediaType ||
            'IMAGE'

        overlayEnabled =
            values.questionOverlayEnabled

        overlayColor =
            values.questionOverlayColor ||
            'rgba(0,0,0,0.20)'
    }

    if (
        previewScreen ===
        'RESULT'
    ) {
        backgroundColor =
            values.resultBackgroundColor ||
            '#000000'

        backgroundUrl =
            values.resultBackgroundUrl

        backgroundVideoUrl =
            values.resultBackgroundVideoUrl

        mediaType =
            values.resultMediaType ||
            'IMAGE'

        overlayEnabled =
            values.resultOverlayEnabled

        overlayColor =
            values.resultOverlayColor ||
            'rgba(0,0,0,0.20)'
    }



    // ==================================================
    // FORMULÁRIO
    // ==================================================

    if (
        previewScreen ===
        'FORM'
    ) {
        backgroundColor =
            values.formPageBackgroundColor ||
            '#050505'

        backgroundUrl =
            values.formPageBackgroundUrl ||
            ''

        backgroundVideoUrl =
            ''

        mediaType =
            'IMAGE'

        const overlayOpacity =
            Math.max(
                0,
                Math.min(
                    100,
                    Number(
                        values.formPageOverlayOpacity ??
                        0,
                    ),
                ),
            ) / 100

        const overlayBaseColor =
            values.formPageOverlayColor ||
            '#000000'

        const overlayHex =
            Math.round(
                overlayOpacity * 255,
            )
                .toString(16)
                .padStart(
                    2,
                    '0',
                )

        overlayEnabled =
            overlayOpacity > 0

        overlayColor =
            overlayOpacity > 0
                ? `${overlayBaseColor}${overlayHex}`
                : 'transparent'
    }

    const containerStyle = {
        width:
            screenWidth,

        height:
            screenHeight,

        position:
            'relative',

        overflow:
            'hidden',

        backgroundColor,

        backgroundSize:
            'cover',

        backgroundPosition:
            'center',

        fontFamily:
            values.fontFamily ||
            'Arial',

        boxSizing:
            'border-box',
    }

    if (
        mediaType ===
        'IMAGE' &&
        backgroundUrl &&
        previewScreen !==
        'FORM'
    ) {
        containerStyle.backgroundImage =
            `url("${getMediaUrl(
                backgroundUrl,
            )}")`
    }

    // ==================================================
    // START
    // ==================================================

    const renderStart =
        () => {
            const fontFamily =
                values.fontFamily ||
                'Arial'

            const getLogoAlignment =
                (position) => {
                    const normalized =
                        String(
                            position ||
                            'CENTER',
                        ).toUpperCase()

                    if (
                        normalized.includes(
                            'LEFT',
                        )
                    ) {
                        return 'flex-start'
                    }

                    if (
                        normalized.includes(
                            'RIGHT',
                        )
                    ) {
                        return 'flex-end'
                    }

                    return 'center'
                }
            const horizontal =
                values.startScreenContentPosition ||
                'CENTER'

            const vertical =
                values.startScreenContentVerticalPosition ||
                'CENTER'

            const offsetX =
                Number(
                    values.startScreenContentOffsetX ||
                    0,
                )

            const offsetY =
                Number(
                    values.startScreenContentOffsetY ||
                    0,
                )

            const textAlign =
                values.startScreenTextAlign ||
                (
                    horizontal ===
                        'LEFT'
                        ? 'left'
                        : horizontal ===
                            'RIGHT'
                            ? 'right'
                            : 'center'
                )

            const buttonAlign =
                values.startScreenButtonAlign ||
                textAlign

            // ==================================================
            // POSIÇÃO LIVRE DO BOTÃO
            // ==================================================

            const buttonPositionMode =
                values.startScreenButtonPositionMode ||
                'FLOW'

            const buttonPositionX =
                Number(
                    values.startScreenButtonPositionX ??
                    50,
                )

            const buttonPositionY =
                Number(
                    values.startScreenButtonPositionY ??
                    75,
                )

            const isFreeButton =
                buttonPositionMode ===
                'FREE'

            // ==================================================
            // ALINHAMENTO DO CONTEÚDO
            // ==================================================

            const getHorizontalAlignment =
                () => {
                    if (
                        horizontal ===
                        'LEFT'
                    ) {
                        return 'flex-start'
                    }

                    if (
                        horizontal ===
                        'RIGHT'
                    ) {
                        return 'flex-end'
                    }

                    return 'center'
                }

            const getVerticalAlignment =
                () => {
                    if (
                        vertical ===
                        'TOP'
                    ) {
                        return 'flex-start'
                    }

                    if (
                        vertical ===
                        'BOTTOM'
                    ) {
                        return 'flex-end'
                    }

                    return 'center'
                }

            const getButtonAlignment =
                () => {
                    if (
                        buttonAlign ===
                        'left'
                    ) {
                        return 'flex-start'
                    }

                    if (
                        buttonAlign ===
                        'right'
                    ) {
                        return 'flex-end'
                    }

                    return 'center'
                }

            // ==================================================
            // BOTÃO
            // ==================================================

            const renderButton =
                (
                    free = false,
                ) => (
                    <button
                        type="button"
                        style={{
                            position:
                                free
                                    ? 'absolute'
                                    : 'relative',

                            left:
                                free
                                    ? `${buttonPositionX}%`
                                    : undefined,

                            top:
                                free
                                    ? `${buttonPositionY}%`
                                    : undefined,

                            transform:
                                free
                                    ? 'translate(-50%, -50%)'
                                    : undefined,

                            width:
                                px(
                                    values.startScreenButtonWidth,
                                    360,
                                ),

                            maxWidth:
                                free
                                    ? 'none'
                                    : '100%',

                            height:
                                px(
                                    values.startScreenButtonHeight,
                                    72,
                                ),

                            border:
                                0,

                            borderRadius:
                                px(
                                    values.startScreenButtonBorderRadius,
                                    36,
                                ),

                            background:
                                values.startScreenPrimaryColor ||
                                '#e5cf70',

                            color:
                                values.startScreenButtonTextColor ||
                                '#000000',

                            fontSize:
                                px(
                                    values.startScreenButtonFontSize,
                                    24,
                                ),

                            fontWeight:
                                800,

                            cursor:
                                'pointer',

                            zIndex:
                                free
                                    ? 30
                                    : 1,

                            whiteSpace:
                                'nowrap',
                        }}
                    >
                        {values.startScreenButtonText ||
                            'COMEÇAR'}
                    </button>
                )

            return (
                <div
                    style={{
                        ...containerStyle,

                        background:
                            values.startScreenBackgroundColor ||
                            '#000000',

                        fontFamily,
                    }}
                >
                    {values.startScreenMediaType ===
                        'VIDEO' &&
                        values.startScreenBackgroundVideoUrl ? (
                        <video
                            src={
                                getMediaUrl(
                                    values.startScreenBackgroundVideoUrl,
                                )
                            }
                            autoPlay
                            muted
                            loop
                            playsInline
                            style={{
                                position:
                                    'absolute',
                                inset:
                                    0,
                                width:
                                    '100%',
                                height:
                                    '100%',
                                objectFit:
                                    'cover',
                                zIndex:
                                    0,
                            }}
                        />
                    ) : values.startScreenBackgroundUrl ? (
                        <img
                            src={
                                getMediaUrl(
                                    values.startScreenBackgroundUrl,
                                )
                            }
                            alt=""
                            style={{
                                position:
                                    'absolute',
                                inset:
                                    0,
                                width:
                                    '100%',
                                height:
                                    '100%',
                                objectFit:
                                    'cover',
                                zIndex:
                                    0,
                            }}
                        />
                    ) : null}

                    {values.startScreenOverlayEnabled && (
                        <div
                            style={{
                                position:
                                    'absolute',
                                inset:
                                    0,
                                background:
                                    values.startScreenOverlayColor ||
                                    'rgba(0,0,0,0.35)',
                                zIndex:
                                    1,
                                pointerEvents:
                                    'none',
                            }}
                        />
                    )}

                    {/* ======================================= */}
                    {/* CONTEÚDO */}
                    {/* ======================================= */}

                    <div
                        style={{
                            position:
                                'relative',

                            zIndex:
                                3,

                            width:
                                '100%',

                            height:
                                '100%',

                            display:
                                'flex',

                            justifyContent:
                                getHorizontalAlignment(),

                            alignItems:
                                getVerticalAlignment(),

                            padding:
                                px(
                                    70,
                                    70,
                                ),

                            boxSizing:
                                'border-box',

                            pointerEvents:
                                'none',
                        }}
                    >
                        <div
                            style={{
                                width:
                                    px(
                                        values.startScreenCardWidth,
                                        760,
                                    ),

                                maxWidth:
                                    '100%',

                                boxSizing:
                                    'border-box',

                                transform:
                                    `translate(${offsetX}px, ${offsetY}px)`,

                                textAlign,

                                background:
                                    values.startScreenCardEnabled
                                        ? (
                                            values.startScreenCardBackground ||
                                            'rgba(0,0,0,0.65)'
                                        )
                                        : 'transparent',

                                border:
                                    values.startScreenCardEnabled
                                        ? `${Number(
                                            values.startScreenCardBorderWidth ||
                                            0,
                                        )}px solid ${values.startScreenCardBorderColor ||
                                        'transparent'
                                        }`
                                        : 'none',

                                borderRadius:
                                    values.startScreenCardEnabled
                                        ? px(
                                            values.startScreenCardBorderRadius,
                                            32,
                                        )
                                        : 0,

                                padding:
                                    values.startScreenCardEnabled
                                        ? px(
                                            40,
                                            40,
                                        )
                                        : 0,

                                pointerEvents:
                                    'auto',
                            }}
                        >
                            {values.startScreenShowLogo &&
                                values.startScreenLogoUrl && (
                                    <div
                                        style={{
                                            width:
                                                '100%',

                                            display:
                                                'flex',

                                            justifyContent:
                                                getLogoAlignment(
                                                    values.startScreenLogoPosition,
                                                ),

                                            marginBottom:
                                                24,
                                        }}
                                    >
                                        <img
                                            src={
                                                getMediaUrl(
                                                    values.startScreenLogoUrl,
                                                )
                                            }
                                            alt=""
                                            style={{
                                                display:
                                                    'block',

                                                width:
                                                    px(
                                                        values.startScreenLogoWidth,
                                                        260,
                                                    ),

                                                maxWidth:
                                                    '100%',

                                                height:
                                                    'auto',

                                                objectFit:
                                                    'contain',
                                            }}
                                        />
                                    </div>
                                )}

                            {values.startScreenShowTitle !==
                                false && (
                                    <div
                                        style={{
                                            color:
                                                values.startScreenTitleColor ||
                                                values.startScreenTextColor ||
                                                '#ffffff',

                                            fontSize:
                                                px(
                                                    values.startScreenTitleFontSize,
                                                    54,
                                                ),

                                            fontWeight:
                                                900,

                                            lineHeight:
                                                1.05,

                                            whiteSpace:
                                                'pre-line',
                                        }}
                                    >
                                        {values.startScreenTitle ||
                                            'QUIZ'}
                                    </div>
                                )}

                            {values.startScreenShowSubtitle !==
                                false && (
                                    <div
                                        style={{
                                            color:
                                                values.startScreenSubtitleColor ||
                                                values.startScreenTextColor ||
                                                '#ffffff',

                                            fontSize:
                                                px(
                                                    values.startScreenSubtitleFontSize,
                                                    26,
                                                ),

                                            fontWeight:
                                                500,

                                            lineHeight:
                                                1.25,

                                            marginTop:
                                                24,

                                            whiteSpace:
                                                'pre-line',
                                        }}
                                    >
                                        {values.startScreenSubtitle ||
                                            'Responda às perguntas e descubra seu resultado.'}
                                    </div>
                                )}

                            {/* BOTÃO NO FLUXO NORMAL */}

                            {!isFreeButton && (
                                <div
                                    style={{
                                        width:
                                            '100%',

                                        display:
                                            'flex',

                                        justifyContent:
                                            getButtonAlignment(),

                                        marginTop:
                                            24,
                                    }}
                                >
                                    {renderButton(
                                        false,
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ======================================= */}
                    {/* BOTÃO LIVRE */}
                    {/* ======================================= */}

                    {isFreeButton &&
                        renderButton(
                            true,
                        )}
                </div>
            )
        }

    // ==================================================
    // QUESTION
    // ==================================================

    const renderQuestion =
        () => {
            const position =
                getPositionStyle(
                    values.questionCardPosition,
                )

            const options = [
                {
                    label:
                        'A',

                    text:
                        '1 dose ou nenhuma',
                },
                {
                    label:
                        'B',

                    text:
                        'De 2 a 3 doses',
                },
                {
                    label:
                        'C',

                    text:
                        '4 doses ou mais',
                },
            ]

            return (
                <div
                    style={{
                        position:
                            'relative',

                        zIndex:
                            3,

                        width:
                            '100%',

                        height:
                            '100%',

                        display:
                            'flex',

                        flexDirection:
                            'column',

                        ...position,

                        padding:
                            20,

                        boxSizing:
                            'border-box',
                    }}
                >
                    <div
                        style={{
                            width:
                                px(
                                    values.questionCardWidth,
                                    620,
                                ),

                            maxWidth:
                                '94%',

                            padding:
                                px(
                                    values.questionCardPadding,
                                    32,
                                ),

                            background:
                                values.questionCardBackground,

                            border:
                                `${px(
                                    values.questionCardBorderWidth,
                                    2,
                                )}px solid ${values.questionCardBorderColor}`,

                            borderRadius:
                                px(
                                    values.questionCardBorderRadius,
                                    32,
                                ),
                        }}
                    >
                        {values.questionShowLogo &&
                            values.questionLogoUrl && (
                                <div
                                    style={{
                                        textAlign:
                                            'center',

                                        marginBottom:
                                            14,
                                    }}
                                >
                                    <img
                                        src={
                                            getMediaUrl(
                                                values.questionLogoUrl,
                                            )
                                        }
                                        alt=""
                                        style={{
                                            width:
                                                px(
                                                    values.questionLogoWidth,
                                                    220,
                                                ),

                                            maxWidth:
                                                '70%',
                                        }}
                                    />
                                </div>
                            )}

                        {values.showQuestionCounter !==
                            false && (
                                <div
                                    style={{
                                        color:
                                            values.questionCounterColor,

                                        fontSize:
                                            px(
                                                values.questionCounterFontSize,
                                                18,
                                            ),

                                        marginBottom:
                                            8,
                                    }}
                                >
                                    Pergunta 1 de 6
                                </div>
                            )}

                        {values.showProgress !==
                            false && (
                                <div
                                    style={{
                                        width:
                                            '100%',

                                        height:
                                            Math.max(
                                                2,
                                                px(
                                                    values.progressHeight,
                                                    8,
                                                ),
                                            ),

                                        background:
                                            values.progressBackgroundColor,

                                        borderRadius:
                                            px(
                                                values.progressBorderRadius,
                                                8,
                                            ),

                                        overflow:
                                            'hidden',

                                        marginBottom:
                                            16,
                                    }}
                                >
                                    <div
                                        style={{
                                            width:
                                                '16.66%',

                                            height:
                                                '100%',

                                            background:
                                                values.progressColor,
                                        }}
                                    />
                                </div>
                            )}

                        <div
                            style={{
                                color:
                                    values.questionTitleColor,

                                fontSize:
                                    px(
                                        values.questionTitleFontSize,
                                        26,
                                    ),

                                fontWeight:
                                    800,

                                marginBottom:
                                    8,
                            }}
                        >
                            1. O Volume Diário
                        </div>

                        <div
                            style={{
                                color:
                                    values.questionTextColor,

                                fontSize:
                                    px(
                                        values.questionTextFontSize,
                                        34,
                                    ),

                                fontWeight:
                                    700,

                                lineHeight:
                                    1.15,

                                marginBottom:
                                    18,
                            }}
                        >
                            Quantas doses de café ou bebidas com cafeína você toma por dia?
                        </div>

                        <Space
                            direction="vertical"
                            style={{
                                width:
                                    '100%',

                                gap:
                                    px(
                                        values.optionGap,
                                        14,
                                    ),
                            }}
                        >
                            {options.map(
                                (
                                    option,
                                    index,
                                ) => {
                                    const selected =
                                        index ===
                                        1

                                    return (
                                        <div
                                            key={
                                                option.label
                                            }
                                            style={{
                                                minHeight:
                                                    px(
                                                        values.optionHeight,
                                                        72,
                                                    ),

                                                display:
                                                    'flex',

                                                alignItems:
                                                    'center',

                                                gap:
                                                    12,

                                                padding:
                                                    '7px 12px',

                                                background:
                                                    selected
                                                        ? values.optionSelectedBackground
                                                        : values.optionBackground,

                                                color:
                                                    selected
                                                        ? values.optionSelectedTextColor
                                                        : values.optionTextColor,

                                                border:
                                                    `${px(
                                                        values.optionBorderWidth,
                                                        2,
                                                    )}px solid ${selected
                                                        ? values.optionSelectedBorderColor
                                                        : values.optionBorderColor
                                                    }`,

                                                borderRadius:
                                                    px(
                                                        values.optionBorderRadius,
                                                        18,
                                                    ),

                                                fontSize:
                                                    px(
                                                        values.optionFontSize,
                                                        24,
                                                    ),

                                                fontWeight:
                                                    600,
                                            }}
                                        >
                                            <span
                                                style={{
                                                    width:
                                                        28,

                                                    height:
                                                        28,

                                                    flex:
                                                        '0 0 28px',

                                                    display:
                                                        'flex',

                                                    alignItems:
                                                        'center',

                                                    justifyContent:
                                                        'center',

                                                    borderRadius:
                                                        '50%',

                                                    background:
                                                        values.optionLabelBackground,

                                                    color:
                                                        values.optionLabelTextColor,

                                                    fontWeight:
                                                        900,
                                                }}
                                            >
                                                {
                                                    option.label
                                                }
                                            </span>

                                            {
                                                option.text
                                            }
                                        </div>
                                    )
                                },
                            )}
                        </Space>

                        {values.showScore && (
                            <div
                                style={{
                                    marginTop:
                                        12,

                                    color:
                                        values.scoreColor,

                                    fontSize:
                                        px(
                                            values.scoreFontSize,
                                            18,
                                        ),

                                    textAlign:
                                        'right',
                                }}
                            >
                                Pontos: 2
                            </div>
                        )}
                    </div>
                </div>
            )
        }

    // ==================================================
    // RESULT
    // ==================================================

    const renderResult =
        () => {
            const position =
                getPositionStyle(
                    values.resultCardPosition,
                )

            return (
                <div
                    style={{
                        position:
                            'relative',

                        zIndex:
                            3,

                        width:
                            '100%',

                        height:
                            '100%',

                        display:
                            'flex',

                        flexDirection:
                            'column',

                        ...position,

                        padding:
                            22,

                        boxSizing:
                            'border-box',
                    }}
                >
                    <div
                        style={{
                            width:
                                px(
                                    values.resultCardWidth,
                                    760,
                                ),

                            maxWidth:
                                '94%',

                            padding:
                                px(
                                    values.resultCardPadding,
                                    36,
                                ),

                            textAlign:
                                'center',

                            background:
                                values.resultCardBackground,

                            border:
                                `${px(
                                    values.resultCardBorderWidth,
                                    3,
                                )}px solid ${values.resultCardBorderColor}`,

                            borderRadius:
                                px(
                                    values.resultCardBorderRadius,
                                    32,
                                ),
                        }}
                    >
                        {values.resultShowLogo &&
                            values.resultLogoUrl && (
                                <img
                                    src={
                                        getMediaUrl(
                                            values.resultLogoUrl,
                                        )
                                    }
                                    alt=""
                                    style={{
                                        width:
                                            px(
                                                values.resultLogoWidth,
                                                220,
                                            ),

                                        maxWidth:
                                            '70%',

                                        marginBottom:
                                            14,
                                    }}
                                />
                            )}

                        <div
                            style={{
                                color:
                                    values.resultTitleColor,

                                fontSize:
                                    px(
                                        values.resultTitleFontSize,
                                        42,
                                    ),

                                fontWeight:
                                    900,
                            }}
                        >
                            {values.resultTitleText ||
                                'RESULTADO'}
                        </div>

                        {values.resultShowScore && (
                            <div
                                style={{
                                    color:
                                        values.resultScoreColor,

                                    fontSize:
                                        px(
                                            values.resultScoreFontSize,
                                            52,
                                        ),

                                    fontWeight:
                                        900,

                                    marginTop:
                                        10,
                                }}
                            >
                                12 pontos
                            </div>
                        )}

                        <div
                            style={{
                                color:
                                    values.resultTextColor,

                                fontSize:
                                    px(
                                        values.resultTextFontSize,
                                        24,
                                    ),

                                fontWeight:
                                    800,

                                marginTop:
                                    16,
                            }}
                        >
                            Alerta de Sensibilidade
                        </div>

                        <div
                            style={{
                                color:
                                    values.resultTextColor,

                                fontSize:
                                    Math.max(
                                        11,
                                        px(
                                            values.resultTextFontSize,
                                            24,
                                        ) *
                                        0.7,
                                    ),

                                lineHeight:
                                    1.4,

                                marginTop:
                                    12,
                            }}
                        >
                            Seu consumo de cafeína merece atenção,
                            principalmente no meio e no fim da tarde.
                        </div>

                        <button
                            type="button"
                            style={{
                                marginTop:
                                    22,

                                width:
                                    px(
                                        values.resultButtonWidth,
                                        360,
                                    ),

                                maxWidth:
                                    '90%',

                                height:
                                    px(
                                        values.resultButtonHeight,
                                        72,
                                    ),

                                border:
                                    0,

                                borderRadius:
                                    px(
                                        values.resultButtonBorderRadius,
                                        36,
                                    ),

                                background:
                                    values.resultButtonColor,

                                color:
                                    values.resultButtonTextColor,

                                fontSize:
                                    px(
                                        values.resultButtonFontSize,
                                        24,
                                    ),

                                fontWeight:
                                    900,
                            }}
                        >
                            {values.resultButtonText ||
                                'FINALIZAR'}
                        </button>
                    </div>
                </div>
            )
        }


    // ==================================================
    // FORMULÁRIO
    // ==================================================

    const renderForm =
        () => {
            const position =
                getPositionStyle(
                    values.formPageCardPosition ||
                    'CENTER',
                )

            const logoUrl =
                values.formPageLogoUrl ||
                values.logoUrl

            const cardOpacity =
                Math.max(
                    0,
                    Math.min(
                        100,
                        Number(
                            values.formPageCardOpacity ??
                            100,
                        ),
                    ),
                ) / 100

            const inputHeight =
                px(
                    values.formPageInputHeight,
                    48,
                )

            const inputBorderRadius =
                px(
                    values.formPageInputBorderRadius,
                    10,
                )

            const inputFontSize =
                px(
                    values.formPageInputFontSize,
                    16,
                )

            const labelFontSize =
                px(
                    values.formPageLabelFontSize,
                    14,
                )

            const fieldStyle = {
                width:
                    '100%',

                height:
                    inputHeight,

                boxSizing:
                    'border-box',

                display:
                    'flex',

                alignItems:
                    'center',

                padding:
                    '0 14px',

                background:
                    values.formPageInputBackground ||
                    values.formPageInputBackground ||
                    '#ffffff',

                color:
                    values.formPageInputTextColor ||
                    '#111827',

                border:
                    `1px solid ${values.formPageInputBorderColor ||
                    '#d1d5db'
                    }`,

                borderRadius:
                    inputBorderRadius,

                fontSize:
                    inputFontSize,
            }

            const labelStyle = {
                display:
                    'block',

                marginBottom:
                    6,

                color:
                    values.formPageLabelColor ||
                    '#ffffff',

                fontSize:
                    labelFontSize,

                fontWeight:
                    700,

                lineHeight:
                    1.2,
            }

            return (
                <div
                    style={{
                        position:
                            'relative',

                        zIndex:
                            3,

                        width:
                            '100%',

                        height:
                            '100%',

                        display:
                            'flex',

                        flexDirection:
                            'column',

                        ...position,

                        padding:
                            22,

                        boxSizing:
                            'border-box',
                    }}
                >
                    <div
                        style={{
                            width:
                                px(
                                    values.formPageCardWidth,
                                    560,
                                ),

                            maxWidth:
                                '94%',

                            padding:
                                px(
                                    values.formPageCardPadding,
                                    32,
                                ),

                            boxSizing:
                                'border-box',

                            background:
                                values.formPageCardBackground ||
                                'rgba(15,15,15,0.88)',

                            opacity:
                                cardOpacity,

                            border:
                                `${px(
                                    values.formPageCardBorderWidth,
                                    0,
                                )}px solid ${values.formPageCardBorderColor ||
                                'rgba(255,255,255,0.15)'
                                }`,

                            borderRadius:
                                px(
                                    values.formPageCardBorderRadius,
                                    24,
                                ),

                            boxShadow:
                                values.formPageCardShadow !==
                                    false
                                    ? '0 20px 60px rgba(0,0,0,0.35)'
                                    : 'none',

                            textAlign:
                                'center',
                        }}
                    >
                        {/* ================================= */}
                        {/* LOGO */}
                        {/* ================================= */}

                        {values.formPageShowLogo !==
                            false &&
                            logoUrl && (
                                <div
                                    style={{
                                        width:
                                            '100%',

                                        display:
                                            'flex',

                                        justifyContent:
                                            values.formPageLogoHorizontalPosition ===
                                                'LEFT'
                                                ? 'flex-start'
                                                : values.formPageLogoHorizontalPosition ===
                                                    'RIGHT'
                                                    ? 'flex-end'
                                                    : 'center',

                                        marginBottom:
                                            20,
                                    }}
                                >
                                    <img
                                        src={
                                            getMediaUrl(
                                                logoUrl,
                                            )
                                        }
                                        alt=""
                                        style={{
                                            width:
                                                px(
                                                    values.formPageLogoSize,
                                                    180,
                                                ),

                                            maxWidth:
                                                '100%',

                                            height:
                                                'auto',

                                            objectFit:
                                                'contain',
                                        }}
                                    />
                                </div>
                            )}

                        {/* ================================= */}
                        {/* TÍTULO */}
                        {/* ================================= */}

                        {values.formPageShowTitle !==
                            false && (
                                <div
                                    style={{
                                        color:
                                            values.formPageTitleColor ||
                                            '#ffffff',

                                        fontSize:
                                            px(
                                                values.formPageTitleFontSize,
                                                32,
                                            ),

                                        fontWeight:
                                            900,

                                        lineHeight:
                                            1.15,
                                    }}
                                >
                                    {values.formPageTitle ||
                                        'Cadastre-se'}
                                </div>
                            )}

                        {/* ================================= */}
                        {/* SUBTÍTULO */}
                        {/* ================================= */}

                        {values.formPageShowSubtitle !==
                            false && (
                                <div
                                    style={{
                                        color:
                                            values.formPageSubtitleColor ||
                                            '#ffffff',

                                        fontSize:
                                            px(
                                                values.formPageSubtitleFontSize,
                                                18,
                                            ),

                                        marginTop:
                                            10,

                                        lineHeight:
                                            1.4,
                                    }}
                                >
                                    {values.formPageSubtitle ||
                                        'Preencha seus dados para participar'}
                                </div>
                            )}

                        {/* ================================= */}
                        {/* CAMPOS */}
                        {/* ================================= */}

                        <div
                            style={{
                                marginTop:
                                    24,

                                display:
                                    'flex',

                                flexDirection:
                                    'column',

                                gap:
                                    16,

                                textAlign:
                                    'left',
                            }}
                        >
                            {/* NOME */}

                            <div>
                                <div
                                    style={
                                        labelStyle
                                    }
                                >
                                    Nome
                                </div>

                                <div
                                    style={
                                        fieldStyle
                                    }
                                >
                                    <span
                                        style={{
                                            opacity:
                                                0.55,
                                        }}
                                    >
                                        Digite seu nome
                                    </span>
                                </div>
                            </div>

                            {/* E-MAIL */}

                            <div>
                                <div
                                    style={
                                        labelStyle
                                    }
                                >
                                    E-mail
                                </div>

                                <div
                                    style={
                                        fieldStyle
                                    }
                                >
                                    <span
                                        style={{
                                            opacity:
                                                0.55,
                                        }}
                                    >
                                        Digite seu e-mail
                                    </span>
                                </div>
                            </div>

                            {/* TELEFONE */}

                            <div>
                                <div
                                    style={
                                        labelStyle
                                    }
                                >
                                    Telefone
                                </div>

                                <div
                                    style={
                                        fieldStyle
                                    }
                                >
                                    <span
                                        style={{
                                            opacity:
                                                0.55,
                                        }}
                                    >
                                        Digite seu telefone
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* ================================= */}
                        {/* CONSENTIMENTO */}
                        {/* ================================= */}

                        <div
                            style={{
                                display:
                                    'flex',

                                alignItems:
                                    'flex-start',

                                gap:
                                    10,

                                marginTop:
                                    18,

                                textAlign:
                                    'left',

                                color:
                                    values.formPageConsentTextColor ||
                                    '#ffffff',

                                fontSize:
                                    Math.max(
                                        11,
                                        labelFontSize,
                                    ),

                                lineHeight:
                                    1.35,
                            }}
                        >
                            <div
                                style={{
                                    width:
                                        18,

                                    height:
                                        18,

                                    minWidth:
                                        18,

                                    borderRadius:
                                        4,

                                    border:
                                        `1px solid ${values.formPageInputBorderColor ||
                                        '#d1d5db'
                                        }`,

                                    background:
                                        values.formPageInputBackground ||
                                        values.formPageInputBackground ||
                                        '#ffffff',
                                }}
                            />

                            <span>
                                Li e concordo com os termos
                                para participar.
                            </span>
                        </div>

                        {/* ================================= */}
                        {/* BOTÃO */}
                        {/* ================================= */}

                        <button
                            type="button"
                            style={{
                                marginTop:
                                    24,

                                width:
                                    '100%',

                                height:
                                    px(
                                        values.formPageButtonHeight,
                                        56,
                                    ),

                                padding:
                                    '0 20px',

                                border:
                                    0,

                                borderRadius:
                                    px(
                                        values.formPageButtonBorderRadius,
                                        28,
                                    ),

                                background:
                                    values.formPageButtonColor ||
                                    '#1677ff',

                                color:
                                    values.formPageButtonTextColor ||
                                    '#ffffff',

                                fontSize:
                                    px(
                                        values.formPageButtonFontSize,
                                        18,
                                    ),

                                fontWeight:
                                    900,

                                cursor:
                                    'default',
                            }}
                        >
                            {values.formPageButtonText ||
                                'COMEÇAR JOGO'}
                        </button>
                    </div>
                </div>
            )
        }



    // ==================================================
    // GERAL
    // ==================================================

    const renderGeneral =
        () => {
            return (
                <div
                    style={{
                        position:
                            'relative',

                        zIndex:
                            2,

                        width:
                            '100%',

                        height:
                            '100%',

                        display:
                            'flex',

                        flexDirection:
                            'column',

                        alignItems:
                            'center',

                        justifyContent:
                            'center',

                        textAlign:
                            'center',

                        fontFamily:
                            values.fontFamily ||
                            'Arial',

                        color:
                            '#ffffff',

                        background:
                            '#000000',
                    }}
                >
                    <div
                        style={{
                            fontSize:
                                48,

                            fontWeight:
                                800,

                            marginBottom:
                                16,
                        }}
                    >
                        Preview geral
                    </div>

                    <div
                        style={{
                            fontSize:
                                24,

                            opacity:
                                0.8,
                        }}
                    >
                        {screenWidth}
                        {' x '}
                        {screenHeight}
                    </div>

                    <div
                        style={{
                            fontSize:
                                20,

                            marginTop:
                                8,

                            opacity:
                                0.7,
                        }}
                    >
                        {isHorizontal
                            ? 'Horizontal'
                            : 'Vertical'}
                    </div>

                    <div
                        style={{
                            fontSize:
                                18,

                            marginTop:
                                8,

                            opacity:
                                0.6,
                        }}
                    >
                        Fonte:{' '}
                        {values.fontFamily ||
                            'Arial'}
                    </div>
                </div>
            )
        }

    return (
        <div
            style={{
                width:
                    previewWidth,

                height:
                    previewHeight,

                position:
                    'relative',

                overflow:
                    'hidden',

                borderRadius:
                    14,

                background:
                    '#000000',

                boxShadow:
                    '0 8px 30px rgba(0,0,0,0.18)',
            }}
        >
            <div
                style={{
                    ...containerStyle,

                    position:
                        'absolute',

                    left:
                        0,

                    top:
                        0,

                    transform:
                        `scale(${scale})`,

                    transformOrigin:
                        'top left',
                }}

            >
                {/* ================================= */}
                {/* FUNDO DO FORMULÁRIO */}
                {/* ================================= */}

                {previewScreen ===
                    'FORM' &&
                    backgroundUrl && (
                        <div
                            style={{
                                position:
                                    'absolute',

                                inset:
                                    Number(
                                        values.formPageBackgroundBlur ||
                                        0,
                                    ) > 0
                                        ? -30
                                        : 0,

                                backgroundImage:
                                    `url("${getMediaUrl(
                                        backgroundUrl,
                                    )}")`,

                                backgroundSize:
                                    'cover',

                                backgroundPosition:
                                    'center',

                                backgroundRepeat:
                                    'no-repeat',

                                filter:
                                    Number(
                                        values.formPageBackgroundBlur ||
                                        0,
                                    ) > 0
                                        ? `blur(${Number(
                                            values.formPageBackgroundBlur,
                                        )}px)`
                                        : 'none',

                                zIndex:
                                    0,

                                pointerEvents:
                                    'none',
                            }}
                        />
                    )}

                {mediaType ===
                    'VIDEO' &&
                    backgroundVideoUrl && (
                        <video
                            key={
                                backgroundVideoUrl
                            }
                            src={
                                getMediaUrl(
                                    backgroundVideoUrl,
                                )
                            }
                            autoPlay
                            muted
                            loop
                            playsInline
                            style={{
                                position:
                                    'absolute',

                                inset:
                                    0,

                                width:
                                    '100%',

                                height:
                                    '100%',

                                objectFit:
                                    'cover',

                                zIndex:
                                    0,
                            }}
                        />
                    )}

                {overlayEnabled && (
                    <div
                        style={{
                            position:
                                'absolute',

                            inset:
                                0,

                            background:
                                overlayColor,

                            zIndex:
                                1,
                        }}
                    />
                )}
                {previewScreen ===
                    'GENERAL' &&
                    renderGeneral()}

                {previewScreen ===
                    'START' &&
                    renderStart()}

                {previewScreen ===
                    'QUESTION' &&
                    renderQuestion()}

                {previewScreen ===
                    'RESULT' &&
                    renderResult()}

                {previewScreen ===
                    'FORM' &&
                    renderForm()}
            </div>
        </div>
    )
}

// ======================================================
// PÁGINA
// ======================================================

const QuizAppearance = () => {
    const [
        form,
    ] = Form.useForm()

    const [
        searchParams,
    ] = useSearchParams()

    const gameId =
        searchParams.get(
            'gameId',
        )

    const [
        loading,
        setLoading,
    ] = useState(true)

    const [
        saving,
        setSaving,
    ] = useState(false)

    const [
        error,
        setError,
    ] = useState(null)

    const [
        game,
        setGame,
    ] = useState(null)

    const [
        uploading,
        setUploading,
    ] = useState(null)

    const [
        previewScreen,
        setPreviewScreen,
    ] = useState(
        'GENERAL',
    )

    const values =
        Form.useWatch(
            [],
            form,
        ) || {}

    // ==================================================
    // CARREGAR
    // ==================================================

    const loadConfig =
        useCallback(
            async () => {
                if (!gameId) {
                    setError(
                        'Nenhum Quiz foi selecionado.',
                    )

                    setLoading(
                        false,
                    )

                    return
                }

                try {
                    setLoading(
                        true,
                    )

                    setError(
                        null,
                    )

                    const response =
                        await fetch.get(
                            `/quiz-games/${gameId}`,
                        )

                    const payload =
                        response?.data ||
                        response

                    setGame(
                        payload?.game ||
                        null,
                    )

                    form.setFieldsValue({
                        ...(payload?.config || {}),

                        // ==============================================
                        // FORMULÁRIO / LEAD PAGE
                        // Campos pertencentes ao Game
                        // ==============================================

                        formPageEnabled:
                            payload?.game?.formPageEnabled ??
                            true,

                        formPageBackgroundUrl:
                            payload?.game?.formPageBackgroundUrl ||
                            '',

                        formPageBackgroundColor:
                            payload?.game?.formPageBackgroundColor ||
                            '#050505',

                        formPageOverlayColor:
                            payload?.game?.formPageOverlayColor ||
                            '#000000',

                        formPageOverlayOpacity:
                            Number(
                                payload?.game?.formPageOverlayOpacity ??
                                0,
                            ),

                        formPageBackgroundBlur:
                            Number(
                                payload?.game?.formPageBackgroundBlur ??
                                0,
                            ),

                        formPageLogoUrl:
                            payload?.game?.formPageLogoUrl ||
                            '',

                        formPageLogoSize:
                            Number(
                                payload?.game?.formPageLogoSize ||
                                180,
                            ),

                        formPageTitle:
                            payload?.game?.formPageTitle ||
                            'Preencha seus dados',

                        formPageSubtitle:
                            payload?.game?.formPageSubtitle ||
                            '',

                        formPageTitleColor:
                            payload?.game?.formPageTitleColor ||
                            '#ffffff',

                        formPageSubtitleColor:
                            payload?.game?.formPageSubtitleColor ||
                            '#ffffff',

                        formPageTitleFontSize:
                            Number(
                                payload?.game?.formPageTitleFontSize ||
                                32,
                            ),

                        formPageSubtitleFontSize:
                            Number(
                                payload?.game?.formPageSubtitleFontSize ||
                                18,
                            ),

                        formPageCardBackground:
                            payload?.game?.formPageCardBackground ||
                            '#ffffff',

                        formPageCardOpacity:
                            Number(
                                payload?.game?.formPageCardOpacity ??
                                96,
                            ),

                        formPageCardBorderColor:
                            payload?.game?.formPageCardBorderColor ||
                            '#ffffff',

                        formPageCardBorderWidth:
                            Number(
                                payload?.game?.formPageCardBorderWidth ||
                                0,
                            ),

                        formPageCardBorderRadius:
                            Number(
                                payload?.game?.formPageCardBorderRadius ||
                                18,
                            ),

                        formPageCardShadow:
                            payload?.game?.formPageCardShadow ??
                            true,

                        formPageCardWidth:
                            Number(
                                payload?.game?.formPageCardWidth ||
                                560,
                            ),

                        formPageCardPadding:
                            Number(
                                payload?.game?.formPageCardPadding ||
                                32,
                            ),

                        formPageCardPosition:
                            payload?.game?.formPageCardPosition ||
                            'CENTER',

                        formPageLabelColor:
                            payload?.game?.formPageLabelColor ||
                            '#374151',

                        formPageLabelFontSize:
                            Number(
                                payload?.game?.formPageLabelFontSize ||
                                14,
                            ),

                        formPageInputBackground:
                            payload?.game?.formPageInputBackground ||
                            '#ffffff',

                        formPageInputTextColor:
                            payload?.game?.formPageInputTextColor ||
                            '#111827',

                        formPageInputBorderColor:
                            payload?.game?.formPageInputBorderColor ||
                            '#d1d5db',

                        formPageInputBorderRadius:
                            Number(
                                payload?.game?.formPageInputBorderRadius ||
                                10,
                            ),

                        formPageInputHeight:
                            Number(
                                payload?.game?.formPageInputHeight ||
                                46,
                            ),

                        formPageInputFontSize:
                            Number(
                                payload?.game?.formPageInputFontSize ||
                                16,
                            ),

                        formPageConsentTextColor:
                            payload?.game?.formPageConsentTextColor ||
                            '#4b5563',

                        formPageButtonText:
                            payload?.game?.formPageButtonText ||
                            'Começar jogo',

                        formPageButtonColor:
                            payload?.game?.formPageButtonColor ||
                            '#f97316',

                        formPageButtonTextColor:
                            payload?.game?.formPageButtonTextColor ||
                            '#ffffff',

                        formPageButtonHeight:
                            Number(
                                payload?.game?.formPageButtonHeight ||
                                54,
                            ),

                        formPageButtonBorderRadius:
                            Number(
                                payload?.game?.formPageButtonBorderRadius ||
                                10,
                            ),
                    })
                } catch (
                err
                ) {
                    console.error(
                        '[QUIZ APPEARANCE] Erro ao carregar:',
                        err,
                    )

                    setError(
                        getErrorMessage(
                            err,
                            'Não foi possível carregar a personalização.',
                        ),
                    )
                } finally {
                    setLoading(
                        false,
                    )
                }
            },
            [
                gameId,
                form,
            ],
        )

    // ==================================================
    // UPLOAD
    // ==================================================

    const handleUpload =
        async (
            fieldName,
            file,
            type,
        ) => {
            try {
                setUploading(
                    fieldName,
                )

                const response =
                    type ===
                        'video'
                        ? await UploadService.video(
                            file,
                        )
                        : await UploadService.image(
                            file,
                        )

                const url =
                    getUploadUrl(
                        response,
                    )

                if (!url) {
                    throw new Error(
                        'A API não retornou a URL do arquivo.',
                    )
                }

                form.setFieldValue(
                    fieldName,
                    url,
                )

                message.success(
                    type ===
                        'video'
                        ? 'Vídeo enviado com sucesso.'
                        : 'Imagem enviada com sucesso.',
                )
            } catch (
            err
            ) {
                console.error(
                    '[QUIZ APPEARANCE] Upload:',
                    err,
                )

                message.error(
                    getErrorMessage(
                        err,
                        'Erro ao enviar arquivo.',
                    ),
                )
            } finally {
                setUploading(
                    null,
                )
            }

            return false
        }

    // ==================================================
    // SALVAR
    // ==================================================

    const handleSave =
        async () => {
            try {
                const allValues =
                    await form.validateFields()

                setSaving(
                    true,
                )

                // ==============================================
                // CAMPOS DO FORMULÁRIO
                // Pertencem ao model Game
                // ==============================================

                const formPageFields = [
                    // ==========================================
                    // GERAL / FUNDO
                    // ==========================================

                    'formPageEnabled',

                    'formPageBackgroundUrl',
                    'formPageBackgroundColor',

                    'formPageOverlayColor',
                    'formPageOverlayOpacity',
                    'formPageBackgroundBlur',

                    // ==========================================
                    // LOGO
                    // ==========================================

                    'formPageShowLogo',
                    'formPageLogoUrl',
                    'formPageLogoSize',

                    'formPageLogoVerticalPosition',
                    'formPageLogoHorizontalPosition',

                    // ==========================================
                    // TÍTULO
                    // ==========================================

                    'formPageShowTitle',
                    'formPageTitle',
                    'formPageTitleColor',
                    'formPageTitleFontSize',
                    'formPageTitleFontWeight',

                    // ==========================================
                    // SUBTÍTULO
                    // ==========================================

                    'formPageShowSubtitle',
                    'formPageSubtitle',
                    'formPageSubtitleColor',
                    'formPageSubtitleFontSize',
                    'formPageSubtitleFontWeight',

                    // ==========================================
                    // CARD
                    // ==========================================

                    'formPageCardBackground',
                    'formPageCardOpacity',

                    'formPageCardBorderColor',
                    'formPageCardBorderWidth',
                    'formPageCardBorderRadius',

                    'formPageCardShadow',

                    'formPageCardWidth',
                    'formPageCardPadding',
                    'formPageCardPosition',

                    // ==========================================
                    // LABELS
                    // ==========================================

                    'formPageLabelColor',
                    'formPageLabelFontSize',
                    'formPageLabelFontWeight',

                    // ==========================================
                    // INPUTS
                    // ==========================================

                    'formPageInputBackground',
                    'formPageInputTextColor',
                    'formPageInputPlaceholderColor',

                    'formPageInputBorderColor',
                    'formPageInputFocusColor',

                    'formPageInputBorderRadius',
                    'formPageInputHeight',
                    'formPageInputFontSize',

                    // ==========================================
                    // CONSENTIMENTO
                    // ==========================================

                    'formPageConsentTextColor',

                    // ==========================================
                    // BOTÃO
                    // ==========================================

                    'formPageButtonText',
                    'formPageButtonColor',
                    'formPageButtonTextColor',
                    'formPageButtonHeight',
                    'formPageButtonBorderRadius',
                ]

                // ==============================================
                // PAYLOAD DO GAME
                // ==============================================

                const gamePayload = {}

                formPageFields.forEach(
                    (field) => {
                        if (
                            Object.prototype
                                .hasOwnProperty.call(
                                    allValues,
                                    field,
                                )
                        ) {
                            gamePayload[field] =
                                allValues[field]
                        }
                    },
                )

                // ==============================================
                // PAYLOAD DO QUIZ
                // Remove os campos pertencentes ao Game
                // ==============================================

                const quizPayload = {
                    ...allValues,
                }

                formPageFields.forEach(
                    (field) => {
                        delete quizPayload[
                            field
                        ]
                    },
                )

                // ==============================================
                // SALVAR QUIZ
                // ==============================================

                await fetch.put(
                    `/quiz-games/${gameId}/config`,
                    quizPayload,
                )

                // ==============================================
                // SALVAR FORMULÁRIO NO GAME
                // ==============================================

                await fetch.patch(
                    `/games/${gameId}`,
                    gamePayload,
                )

                message.success(
                    'Personalização salva com sucesso.',
                )

                await loadConfig()
            } catch (
            err
            ) {
                if (
                    err?.errorFields
                ) {
                    return
                }

                console.error(
                    '[QUIZ APPEARANCE] Erro ao salvar:',
                    err,
                )

                message.error(
                    getErrorMessage(
                        err,
                        'Não foi possível salvar a personalização.',
                    ),
                )
            } finally {
                setSaving(
                    false,
                )
            }
        }

    // ==================================================
    // INIT
    // ==================================================

    useEffect(
        () => {
            loadConfig()
        },
        [
            loadConfig,
        ],
    )

    // ==================================================
    // LOADING
    // ==================================================

    if (loading) {
        return (
            <div
                style={{
                    minHeight:
                        450,

                    display:
                        'flex',

                    alignItems:
                        'center',

                    justifyContent:
                        'center',
                }}
            >
                <Spin
                    size="large"
                />
            </div>
        )
    }

    if (error) {
        return (
            <Card>
                <Alert
                    type="error"
                    showIcon
                    message="Erro ao carregar personalização"
                    description={
                        error
                    }
                />

                <Button
                    type="primary"
                    icon={
                        <ReloadOutlined />
                    }
                    onClick={
                        loadConfig
                    }
                    style={{
                        marginTop:
                            16,
                    }}
                >
                    Tentar novamente
                </Button>
            </Card>
        )
    }

    // ==================================================
    // VIEW
    // ==================================================

    return (
        <Form
            form={
                form
            }
            layout="vertical"
        >
            <Card
                style={{
                    marginBottom:
                        20,
                }}
            >
                <Row
                    gutter={[
                        16,
                        16,
                    ]}
                    justify="space-between"
                    align="middle"
                >
                    <Col>
                        <Title
                            level={2}
                            style={{
                                margin:
                                    0,
                            }}
                        >
                            Personalização do Quiz
                        </Title>

                        <Text
                            type="secondary"
                        >
                            {game?.name ||
                                'Quiz'}
                        </Text>
                    </Col>

                    <Col>
                        <Space
                            wrap
                        >
                            <Button
                                icon={
                                    <ReloadOutlined />
                                }
                                onClick={
                                    loadConfig
                                }
                            >
                                Recarregar
                            </Button>

                            <Button
                                type="primary"
                                icon={
                                    <SaveOutlined />
                                }
                                loading={
                                    saving
                                }
                                onClick={
                                    handleSave
                                }
                            >
                                Salvar personalização
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Row
                gutter={[
                    20,
                    20,
                ]}
            >
                {/* ==========================================
                    EDITOR
                ========================================== */}

                <Col
                    xs={24}
                    xl={15}
                >
                    <Card>
                        <Tabs
                            defaultActiveKey="general"
                            destroyInactiveTabPane={false}
                            onChange={(key) => {
                                const previewMap = {
                                    general:
                                        'GENERAL',

                                    start:
                                        'START',

                                    questions:
                                        'QUESTION',

                                    result:
                                        'RESULT',

                                    form:
                                        'FORM',
                                }

                                const nextPreview =
                                    previewMap[key]

                                if (nextPreview) {
                                    setPreviewScreen(
                                        nextPreview,
                                    )
                                }
                            }}
                            items={[
                                {
                                    key:
                                        'general',

                                    label:
                                        'Geral',

                                    children: (
                                        <>
                                            <Title
                                                level={4}
                                            >
                                                Identidade visual
                                            </Title>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <Form.Item
                                                        label="Orientação da tela"
                                                        name="orientation"
                                                    >
                                                        <Select
                                                            onChange={(
                                                                value,
                                                            ) => {
                                                                if (
                                                                    value ===
                                                                    'HORIZONTAL'
                                                                ) {
                                                                    form.setFieldsValue({
                                                                        screenWidth:
                                                                            1920,
                                                                        screenHeight:
                                                                            1080,
                                                                    })

                                                                    return
                                                                }

                                                                form.setFieldsValue({
                                                                    screenWidth:
                                                                        1080,
                                                                    screenHeight:
                                                                        1920,
                                                                })
                                                            }}
                                                            options={[
                                                                {
                                                                    value:
                                                                        'VERTICAL',
                                                                    label:
                                                                        'Vertical - 1080 x 1920',
                                                                },
                                                                {
                                                                    value:
                                                                        'HORIZONTAL',
                                                                    label:
                                                                        'Horizontal - 1920 x 1080',
                                                                },
                                                            ]}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={6}
                                                >
                                                    <Form.Item
                                                        label="Largura"
                                                        name="screenWidth"
                                                    >
                                                        <InputNumber
                                                            min={320}
                                                            max={7680}
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={6}
                                                >
                                                    <Form.Item
                                                        label="Altura"
                                                        name="screenHeight"
                                                    >
                                                        <InputNumber
                                                            min={320}
                                                            max={7680}
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Form.Item
                                                label="Fonte"
                                                name="fontFamily"
                                            >
                                                <Select
                                                    options={[
                                                        {
                                                            value:
                                                                'Arial',

                                                            label:
                                                                'Arial',
                                                        },
                                                        {
                                                            value:
                                                                'Helvetica',

                                                            label:
                                                                'Helvetica',
                                                        },
                                                        {
                                                            value:
                                                                'Verdana',

                                                            label:
                                                                'Verdana',
                                                        },
                                                        {
                                                            value:
                                                                'Tahoma',

                                                            label:
                                                                'Tahoma',
                                                        },
                                                        {
                                                            value:
                                                                'Trebuchet MS',

                                                            label:
                                                                'Trebuchet MS',
                                                        },
                                                        {
                                                            value:
                                                                'Georgia',

                                                            label:
                                                                'Georgia',
                                                        },
                                                    ]}
                                                />
                                            </Form.Item>

                                            <Alert
                                                type="info"
                                                showIcon
                                                message={
                                                    values.orientation ===
                                                        'HORIZONTAL'
                                                        ? 'Preview 1920 x 1080'
                                                        : 'Preview 1080 x 1920'
                                                }
                                                description={
                                                    values.orientation ===
                                                        'HORIZONTAL'
                                                        ? 'O preview à direita representa proporcionalmente uma tela horizontal Full HD.'
                                                        : 'O preview à direita representa proporcionalmente uma tela vertical Full HD.'
                                                }
                                            />
                                        </>
                                    ),
                                },

                                // ==================================
                                // START
                                // ==================================

                                {
                                    key:
                                        'start',

                                    label:
                                        'Tela Inicial',

                                    children: (
                                        <>
                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <Form.Item
                                                        label="Tela inicial ativa"
                                                        name="startScreenEnabled"
                                                        valuePropName="checked"
                                                    >
                                                        <Switch />
                                                    </Form.Item>
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <Form.Item
                                                        label="Tipo de fundo"
                                                        name="startScreenMediaType"
                                                    >
                                                        <Select
                                                            options={[
                                                                {
                                                                    value:
                                                                        'COLOR',
                                                                    label:
                                                                        'Cor',
                                                                },
                                                                {
                                                                    value:
                                                                        'IMAGE',
                                                                    label:
                                                                        'Imagem',
                                                                },
                                                                {
                                                                    value:
                                                                        'VIDEO',
                                                                    label:
                                                                        'Vídeo',
                                                                },
                                                            ]}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <ColorField
                                                label="Cor do fundo"
                                                name="startScreenBackgroundColor"
                                            />

                                            <MediaField
                                                form={
                                                    form
                                                }
                                                label="Imagem de fundo"
                                                fieldName="startScreenBackgroundUrl"
                                                type="image"
                                                uploading={
                                                    uploading
                                                }
                                                onUpload={
                                                    handleUpload
                                                }
                                            />

                                            <MediaField
                                                form={
                                                    form
                                                }
                                                label="Vídeo de fundo"
                                                fieldName="startScreenBackgroundVideoUrl"
                                                type="video"
                                                uploading={
                                                    uploading
                                                }
                                                onUpload={
                                                    handleUpload
                                                }
                                            />

                                            <Divider>
                                                Overlay
                                            </Divider>

                                            <Form.Item
                                                label="Usar camada sobre o fundo"
                                                name="startScreenOverlayEnabled"
                                                valuePropName="checked"
                                            >
                                                <Switch />
                                            </Form.Item>

                                            <Form.Item
                                                label="Cor / transparência do overlay"
                                                name="startScreenOverlayColor"
                                            >
                                                <Input
                                                    placeholder="rgba(0,0,0,0.25)"
                                                />
                                            </Form.Item>

                                            <Divider>
                                                Logo
                                            </Divider>

                                            <Form.Item
                                                label="Mostrar logo"
                                                name="startScreenShowLogo"
                                                valuePropName="checked"
                                            >
                                                <Switch />
                                            </Form.Item>

                                            <MediaField
                                                form={
                                                    form
                                                }
                                                label="Logo"
                                                fieldName="startScreenLogoUrl"
                                                type="image"
                                                uploading={
                                                    uploading
                                                }
                                                onUpload={
                                                    handleUpload
                                                }
                                            />

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <Form.Item
                                                        label="Largura da logo"
                                                        name="startScreenLogoWidth"
                                                    >
                                                        <InputNumber
                                                            min={20}
                                                            max={1080}
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <Form.Item
                                                        label="Posição da logo"
                                                        name="startScreenLogoPosition"
                                                    >
                                                        <Select
                                                            options={[
                                                                'TOP_LEFT',
                                                                'TOP_CENTER',
                                                                'TOP_RIGHT',
                                                                'CENTER_LEFT',
                                                                'CENTER',
                                                                'CENTER_RIGHT',
                                                                'BOTTOM_LEFT',
                                                                'BOTTOM_CENTER',
                                                                'BOTTOM_RIGHT',
                                                            ].map(
                                                                (
                                                                    value,
                                                                ) => ({
                                                                    value,
                                                                    label:
                                                                        value,
                                                                }),
                                                            )}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Divider>
                                                Conteúdo
                                            </Divider>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <Form.Item
                                                        label="Posição horizontal"
                                                        name="startScreenContentPosition"
                                                    >
                                                        <Select
                                                            options={[
                                                                {
                                                                    value:
                                                                        'LEFT',
                                                                    label:
                                                                        'Esquerda',
                                                                },
                                                                {
                                                                    value:
                                                                        'CENTER',
                                                                    label:
                                                                        'Centro',
                                                                },
                                                                {
                                                                    value:
                                                                        'RIGHT',
                                                                    label:
                                                                        'Direita',
                                                                },
                                                            ]}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <Form.Item
                                                        label="Posição vertical"
                                                        name="startScreenContentVerticalPosition"
                                                    >
                                                        <Select
                                                            options={[
                                                                {
                                                                    value:
                                                                        'TOP',
                                                                    label:
                                                                        'Topo',
                                                                },
                                                                {
                                                                    value:
                                                                        'CENTER',
                                                                    label:
                                                                        'Centro',
                                                                },
                                                                {
                                                                    value:
                                                                        'BOTTOM',
                                                                    label:
                                                                        'Rodapé',
                                                                },
                                                            ]}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <Form.Item
                                                        label="Alinhamento do texto"
                                                        name="startScreenTextAlign"
                                                    >
                                                        <Select
                                                            options={[
                                                                {
                                                                    value:
                                                                        'left',
                                                                    label:
                                                                        'Esquerda',
                                                                },
                                                                {
                                                                    value:
                                                                        'center',
                                                                    label:
                                                                        'Centro',
                                                                },
                                                                {
                                                                    value:
                                                                        'right',
                                                                    label:
                                                                        'Direita',
                                                                },
                                                            ]}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <Form.Item
                                                        label="Alinhamento do botão"
                                                        name="startScreenButtonAlign"
                                                    >
                                                        <Select
                                                            options={[
                                                                {
                                                                    value:
                                                                        'LEFT',
                                                                    label:
                                                                        'Esquerda',
                                                                },
                                                                {
                                                                    value:
                                                                        'CENTER',
                                                                    label:
                                                                        'Centro',
                                                                },
                                                                {
                                                                    value:
                                                                        'RIGHT',
                                                                    label:
                                                                        'Direita',
                                                                },
                                                            ]}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col
                                                    xs={24}
                                                    md={8}
                                                >
                                                    <Form.Item
                                                        label="Largura do conteúdo"
                                                        name="startScreenContentWidth"
                                                    >
                                                        <InputNumber
                                                            min={200}
                                                            max={1920}
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={8}
                                                >
                                                    <Form.Item
                                                        label="Deslocamento X"
                                                        name="startScreenContentOffsetX"
                                                    >
                                                        <InputNumber
                                                            min={-1920}
                                                            max={1920}
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={8}
                                                >
                                                    <Form.Item
                                                        label="Deslocamento Y"
                                                        name="startScreenContentOffsetY"
                                                    >
                                                        <InputNumber
                                                            min={-1920}
                                                            max={1920}
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Form.Item
                                                label="Mostrar título"
                                                name="startScreenShowTitle"
                                                valuePropName="checked"
                                            >
                                                <Switch />
                                            </Form.Item>

                                            <Form.Item
                                                label="Título"
                                                name="startScreenTitle"
                                            >
                                                <Input />
                                            </Form.Item>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <ColorField
                                                        label="Cor do título"
                                                        name="startScreenTitleColor"
                                                    />
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <Form.Item
                                                        label="Tamanho do título"
                                                        name="startScreenTitleFontSize"
                                                    >
                                                        <InputNumber
                                                            min={10}
                                                            max={200}
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Form.Item
                                                label="Mostrar subtítulo"
                                                name="startScreenShowSubtitle"
                                                valuePropName="checked"
                                            >
                                                <Switch />
                                            </Form.Item>

                                            <Form.Item
                                                label="Subtítulo"
                                                name="startScreenSubtitle"
                                            >
                                                <Input.TextArea
                                                    rows={3}
                                                />
                                            </Form.Item>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <ColorField
                                                        label="Cor do subtítulo"
                                                        name="startScreenSubtitleColor"
                                                    />
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <Form.Item
                                                        label="Tamanho do subtítulo"
                                                        name="startScreenSubtitleFontSize"
                                                    >
                                                        <InputNumber
                                                            min={10}
                                                            max={100}
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Divider>
                                                Card
                                            </Divider>

                                            <Form.Item
                                                label="Usar card"
                                                name="startScreenCardEnabled"
                                                valuePropName="checked"
                                            >
                                                <Switch />
                                            </Form.Item>

                                            <Form.Item
                                                label="Fundo do card"
                                                name="startScreenCardBackground"
                                            >
                                                <Input />
                                            </Form.Item>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col
                                                    xs={24}
                                                    md={8}
                                                >
                                                    <ColorField
                                                        label="Borda"
                                                        name="startScreenCardBorderColor"
                                                    />
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={8}
                                                >
                                                    <Form.Item
                                                        label="Espessura"
                                                        name="startScreenCardBorderWidth"
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={8}
                                                >
                                                    <Form.Item
                                                        label="Arredondamento"
                                                        name="startScreenCardBorderRadius"
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Form.Item
                                                label="Largura do card"
                                                name="startScreenCardWidth"
                                            >
                                                <InputNumber
                                                    min={200}
                                                    max={1080}
                                                    addonAfter="px"
                                                    style={{
                                                        width:
                                                            '100%',
                                                    }}
                                                />
                                            </Form.Item>

                                            <Divider>
                                                Botão
                                            </Divider>

                                            <Form.Item
                                                label="Posicionamento do botão"
                                                name="startScreenButtonPositionMode"
                                            >
                                                <Select
                                                    options={[
                                                        {
                                                            value: 'FLOW',
                                                            label: 'Normal - acompanha o conteúdo',
                                                        },
                                                        {
                                                            value: 'FREE',
                                                            label: 'Livre - escolher posição X / Y',
                                                        },
                                                    ]}
                                                />
                                            </Form.Item>

                                            <Form.Item
                                                noStyle
                                                shouldUpdate={(
                                                    previousValues,
                                                    currentValues,
                                                ) =>
                                                    previousValues
                                                        .startScreenButtonPositionMode !==
                                                    currentValues
                                                        .startScreenButtonPositionMode
                                                }
                                            >
                                                {({
                                                    getFieldValue,
                                                }) => {
                                                    const positionMode =
                                                        getFieldValue(
                                                            'startScreenButtonPositionMode',
                                                        ) || 'FLOW'

                                                    if (
                                                        positionMode !==
                                                        'FREE'
                                                    ) {
                                                        return null
                                                    }

                                                    return (
                                                        <>
                                                            <Alert
                                                                type="info"
                                                                showIcon
                                                                message="Posição livre"
                                                                description="X controla a posição horizontal e Y controla a posição vertical do botão. Os valores vão de 0% a 100% da tela."
                                                                style={{
                                                                    marginBottom: 16,
                                                                }}
                                                            />

                                                            <Row
                                                                gutter={[
                                                                    16,
                                                                    0,
                                                                ]}
                                                            >
                                                                <Col
                                                                    xs={24}
                                                                    md={12}
                                                                >
                                                                    <Form.Item
                                                                        label="Posição horizontal (X)"
                                                                        name="startScreenButtonPositionX"
                                                                    >
                                                                        <InputNumber
                                                                            min={0}
                                                                            max={100}
                                                                            addonAfter="%"
                                                                            style={{
                                                                                width:
                                                                                    '100%',
                                                                            }}
                                                                        />
                                                                    </Form.Item>
                                                                </Col>

                                                                <Col
                                                                    xs={24}
                                                                    md={12}
                                                                >
                                                                    <Form.Item
                                                                        label="Posição vertical (Y)"
                                                                        name="startScreenButtonPositionY"
                                                                    >
                                                                        <InputNumber
                                                                            min={0}
                                                                            max={100}
                                                                            addonAfter="%"
                                                                            style={{
                                                                                width:
                                                                                    '100%',
                                                                            }}
                                                                        />
                                                                    </Form.Item>
                                                                </Col>
                                                            </Row>
                                                        </>
                                                    )
                                                }}
                                            </Form.Item>

                                            <Form.Item
                                                label="Texto do botão"
                                                name="startScreenButtonText"
                                            >
                                                <Input />
                                            </Form.Item>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <ColorField
                                                        label="Cor do botão"
                                                        name="startScreenPrimaryColor"
                                                    />
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <ColorField
                                                        label="Cor do texto"
                                                        name="startScreenButtonTextColor"
                                                    />
                                                </Col>
                                            </Row>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col span={6}>
                                                    <Form.Item
                                                        label="Largura"
                                                        name="startScreenButtonWidth"
                                                    >
                                                        <InputNumber
                                                            min={50}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={6}>
                                                    <Form.Item
                                                        label="Altura"
                                                        name="startScreenButtonHeight"
                                                    >
                                                        <InputNumber
                                                            min={20}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={6}>
                                                    <Form.Item
                                                        label="Raio"
                                                        name="startScreenButtonBorderRadius"
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={6}>
                                                    <Form.Item
                                                        label="Fonte"
                                                        name="startScreenButtonFontSize"
                                                    >
                                                        <InputNumber
                                                            min={10}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        </>
                                    ),
                                },

                                // ==================================
                                // QUESTIONS
                                // ==================================

                                {
                                    key:
                                        'questions',

                                    label:
                                        'Perguntas',

                                    children: (
                                        <>
                                            <Form.Item
                                                label="Tipo de fundo"
                                                name="questionMediaType"
                                            >
                                                <Select
                                                    options={[
                                                        {
                                                            value:
                                                                'COLOR',
                                                            label:
                                                                'Cor',
                                                        },
                                                        {
                                                            value:
                                                                'IMAGE',
                                                            label:
                                                                'Imagem',
                                                        },
                                                        {
                                                            value:
                                                                'VIDEO',
                                                            label:
                                                                'Vídeo',
                                                        },
                                                    ]}
                                                />
                                            </Form.Item>

                                            <ColorField
                                                label="Cor do fundo"
                                                name="questionBackgroundColor"
                                            />

                                            <MediaField
                                                form={
                                                    form
                                                }
                                                label="Imagem de fundo"
                                                fieldName="questionBackgroundUrl"
                                                type="image"
                                                uploading={
                                                    uploading
                                                }
                                                onUpload={
                                                    handleUpload
                                                }
                                            />

                                            <MediaField
                                                form={
                                                    form
                                                }
                                                label="Vídeo de fundo"
                                                fieldName="questionBackgroundVideoUrl"
                                                type="video"
                                                uploading={
                                                    uploading
                                                }
                                                onUpload={
                                                    handleUpload
                                                }
                                            />

                                            <Divider>
                                                Overlay
                                            </Divider>

                                            <Form.Item
                                                label="Usar overlay"
                                                name="questionOverlayEnabled"
                                                valuePropName="checked"
                                            >
                                                <Switch />
                                            </Form.Item>

                                            <Form.Item
                                                label="Overlay"
                                                name="questionOverlayColor"
                                            >
                                                <Input />
                                            </Form.Item>

                                            <Divider>
                                                Logo
                                            </Divider>

                                            <Form.Item
                                                label="Mostrar logo"
                                                name="questionShowLogo"
                                                valuePropName="checked"
                                            >
                                                <Switch />
                                            </Form.Item>

                                            <MediaField
                                                form={
                                                    form
                                                }
                                                label="Logo"
                                                fieldName="questionLogoUrl"
                                                type="image"
                                                uploading={
                                                    uploading
                                                }
                                                onUpload={
                                                    handleUpload
                                                }
                                            />

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col span={12}>
                                                    <Form.Item
                                                        label="Largura"
                                                        name="questionLogoWidth"
                                                    >
                                                        <InputNumber
                                                            min={20}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={12}>
                                                    <Form.Item
                                                        label="Posição"
                                                        name="questionLogoPosition"
                                                    >
                                                        <Select
                                                            options={[
                                                                'TOP_LEFT',
                                                                'TOP_CENTER',
                                                                'TOP_RIGHT',
                                                                'CENTER_LEFT',
                                                                'CENTER',
                                                                'CENTER_RIGHT',
                                                                'BOTTOM_LEFT',
                                                                'BOTTOM_CENTER',
                                                                'BOTTOM_RIGHT',
                                                            ].map(
                                                                (
                                                                    value,
                                                                ) => ({
                                                                    value,
                                                                    label:
                                                                        value,
                                                                }),
                                                            )}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Divider>
                                                Pergunta
                                            </Divider>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col span={12}>
                                                    <ColorField
                                                        label="Cor do título"
                                                        name="questionTitleColor"
                                                    />
                                                </Col>

                                                <Col span={12}>
                                                    <ColorField
                                                        label="Cor da pergunta"
                                                        name="questionTextColor"
                                                    />
                                                </Col>
                                            </Row>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col span={12}>
                                                    <Form.Item
                                                        label="Tamanho do título"
                                                        name="questionTitleFontSize"
                                                    >
                                                        <InputNumber
                                                            min={10}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={12}>
                                                    <Form.Item
                                                        label="Tamanho da pergunta"
                                                        name="questionTextFontSize"
                                                    >
                                                        <InputNumber
                                                            min={10}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Divider>
                                                Card
                                            </Divider>

                                            <Form.Item
                                                label="Fundo"
                                                name="questionCardBackground"
                                            >
                                                <Input />
                                            </Form.Item>

                                            <ColorField
                                                label="Cor da borda"
                                                name="questionCardBorderColor"
                                            />

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col span={8}>
                                                    <Form.Item
                                                        label="Borda"
                                                        name="questionCardBorderWidth"
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={8}>
                                                    <Form.Item
                                                        label="Raio"
                                                        name="questionCardBorderRadius"
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={8}>
                                                    <Form.Item
                                                        label="Padding"
                                                        name="questionCardPadding"
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col span={12}>
                                                    <Form.Item
                                                        label="Largura"
                                                        name="questionCardWidth"
                                                    >
                                                        <InputNumber
                                                            min={200}
                                                            max={1080}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={12}>
                                                    <Form.Item
                                                        label="Posição"
                                                        name="questionCardPosition"
                                                    >
                                                        <Select
                                                            options={[
                                                                {
                                                                    value:
                                                                        'LEFT',
                                                                    label:
                                                                        'Esquerda',
                                                                },
                                                                {
                                                                    value:
                                                                        'CENTER',
                                                                    label:
                                                                        'Centro',
                                                                },
                                                                {
                                                                    value:
                                                                        'RIGHT',
                                                                    label:
                                                                        'Direita',
                                                                },
                                                                {
                                                                    value:
                                                                        'TOP',
                                                                    label:
                                                                        'Topo',
                                                                },
                                                                {
                                                                    value:
                                                                        'BOTTOM',
                                                                    label:
                                                                        'Rodapé',
                                                                },
                                                            ]}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Divider>
                                                Alternativas
                                            </Divider>

                                            <Form.Item
                                                label="Fundo normal"
                                                name="optionBackground"
                                            >
                                                <Input />
                                            </Form.Item>

                                            <Row gutter={[16, 0]}>
                                                <Col span={12}>
                                                    <ColorField
                                                        label="Texto"
                                                        name="optionTextColor"
                                                    />
                                                </Col>

                                                <Col span={12}>
                                                    <ColorField
                                                        label="Borda"
                                                        name="optionBorderColor"
                                                    />
                                                </Col>
                                            </Row>

                                            <Form.Item
                                                label="Fundo selecionado"
                                                name="optionSelectedBackground"
                                            >
                                                <Input />
                                            </Form.Item>

                                            <Row gutter={[16, 0]}>
                                                <Col span={12}>
                                                    <ColorField
                                                        label="Texto selecionado"
                                                        name="optionSelectedTextColor"
                                                    />
                                                </Col>

                                                <Col span={12}>
                                                    <ColorField
                                                        label="Borda selecionada"
                                                        name="optionSelectedBorderColor"
                                                    />
                                                </Col>
                                            </Row>

                                            <Row gutter={[16, 0]}>
                                                <Col span={6}>
                                                    <Form.Item
                                                        label="Borda"
                                                        name="optionBorderWidth"
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={6}>
                                                    <Form.Item
                                                        label="Raio"
                                                        name="optionBorderRadius"
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={6}>
                                                    <Form.Item
                                                        label="Fonte"
                                                        name="optionFontSize"
                                                    >
                                                        <InputNumber
                                                            min={10}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={6}>
                                                    <Form.Item
                                                        label="Altura"
                                                        name="optionHeight"
                                                    >
                                                        <InputNumber
                                                            min={20}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Form.Item
                                                label="Espaço entre alternativas"
                                                name="optionGap"
                                            >
                                                <InputNumber
                                                    min={0}
                                                    style={{
                                                        width:
                                                            '100%',
                                                    }}
                                                />
                                            </Form.Item>

                                            <Row gutter={[16, 0]}>
                                                <Col span={12}>
                                                    <ColorField
                                                        label="Fundo A/B/C"
                                                        name="optionLabelBackground"
                                                    />
                                                </Col>

                                                <Col span={12}>
                                                    <ColorField
                                                        label="Texto A/B/C"
                                                        name="optionLabelTextColor"
                                                    />
                                                </Col>
                                            </Row>

                                            <Divider>
                                                Progresso
                                            </Divider>

                                            <Form.Item
                                                label="Mostrar contador"
                                                name="showQuestionCounter"
                                                valuePropName="checked"
                                            >
                                                <Switch />
                                            </Form.Item>

                                            <Row gutter={[16, 0]}>
                                                <Col span={12}>
                                                    <ColorField
                                                        label="Cor do contador"
                                                        name="questionCounterColor"
                                                    />
                                                </Col>

                                                <Col span={12}>
                                                    <Form.Item
                                                        label="Tamanho"
                                                        name="questionCounterFontSize"
                                                    >
                                                        <InputNumber
                                                            min={8}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Row gutter={[16, 0]}>
                                                <Col span={12}>
                                                    <ColorField
                                                        label="Fundo da barra"
                                                        name="progressBackgroundColor"
                                                    />
                                                </Col>

                                                <Col span={12}>
                                                    <ColorField
                                                        label="Cor da barra"
                                                        name="progressColor"
                                                    />
                                                </Col>
                                            </Row>

                                            <Row gutter={[16, 0]}>
                                                <Col span={12}>
                                                    <Form.Item
                                                        label="Altura da barra"
                                                        name="progressHeight"
                                                    >
                                                        <InputNumber
                                                            min={1}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={12}>
                                                    <Form.Item
                                                        label="Raio da barra"
                                                        name="progressBorderRadius"
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Row gutter={[16, 0]}>
                                                <Col span={12}>
                                                    <ColorField
                                                        label="Cor da pontuação"
                                                        name="scoreColor"
                                                    />
                                                </Col>

                                                <Col span={12}>
                                                    <Form.Item
                                                        label="Tamanho da pontuação"
                                                        name="scoreFontSize"
                                                    >
                                                        <InputNumber
                                                            min={8}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        </>
                                    ),
                                },

                                // ==================================
                                // RESULT
                                // ==================================

                                {
                                    key:
                                        'result',

                                    label:
                                        'Resultado',

                                    children: (
                                        <>
                                            <Form.Item
                                                label="Tipo de fundo"
                                                name="resultMediaType"
                                            >
                                                <Select
                                                    options={[
                                                        {
                                                            value:
                                                                'COLOR',
                                                            label:
                                                                'Cor',
                                                        },
                                                        {
                                                            value:
                                                                'IMAGE',
                                                            label:
                                                                'Imagem',
                                                        },
                                                        {
                                                            value:
                                                                'VIDEO',
                                                            label:
                                                                'Vídeo',
                                                        },
                                                    ]}
                                                />
                                            </Form.Item>

                                            <ColorField
                                                label="Cor do fundo"
                                                name="resultBackgroundColor"
                                            />

                                            <MediaField
                                                form={form}
                                                label="Imagem de fundo"
                                                fieldName="resultBackgroundUrl"
                                                type="image"
                                                uploading={uploading}
                                                onUpload={handleUpload}
                                            />

                                            <MediaField
                                                form={form}
                                                label="Vídeo de fundo"
                                                fieldName="resultBackgroundVideoUrl"
                                                type="video"
                                                uploading={uploading}
                                                onUpload={handleUpload}
                                            />

                                            <Divider>
                                                Overlay
                                            </Divider>

                                            <Form.Item
                                                label="Usar overlay"
                                                name="resultOverlayEnabled"
                                                valuePropName="checked"
                                            >
                                                <Switch />
                                            </Form.Item>

                                            <Form.Item
                                                label="Overlay"
                                                name="resultOverlayColor"
                                            >
                                                <Input />
                                            </Form.Item>

                                            <Divider>
                                                Logo
                                            </Divider>

                                            <Form.Item
                                                label="Mostrar logo"
                                                name="resultShowLogo"
                                                valuePropName="checked"
                                            >
                                                <Switch />
                                            </Form.Item>

                                            <MediaField
                                                form={form}
                                                label="Logo"
                                                fieldName="resultLogoUrl"
                                                type="image"
                                                uploading={uploading}
                                                onUpload={handleUpload}
                                            />

                                            <Row gutter={[16, 0]}>
                                                <Col span={12}>
                                                    <Form.Item
                                                        label="Largura"
                                                        name="resultLogoWidth"
                                                    >
                                                        <InputNumber
                                                            min={20}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={12}>
                                                    <Form.Item
                                                        label="Posição"
                                                        name="resultLogoPosition"
                                                    >
                                                        <Select
                                                            options={[
                                                                'TOP_LEFT',
                                                                'TOP_CENTER',
                                                                'TOP_RIGHT',
                                                                'CENTER_LEFT',
                                                                'CENTER',
                                                                'CENTER_RIGHT',
                                                                'BOTTOM_LEFT',
                                                                'BOTTOM_CENTER',
                                                                'BOTTOM_RIGHT',
                                                            ].map(
                                                                (
                                                                    value,
                                                                ) => ({
                                                                    value,
                                                                    label:
                                                                        value,
                                                                }),
                                                            )}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Divider>
                                                Resultado
                                            </Divider>

                                            <Form.Item
                                                label="Título"
                                                name="resultTitleText"
                                            >
                                                <Input />
                                            </Form.Item>

                                            <Row gutter={[16, 0]}>
                                                <Col span={12}>
                                                    <ColorField
                                                        label="Cor do título"
                                                        name="resultTitleColor"
                                                    />
                                                </Col>

                                                <Col span={12}>
                                                    <Form.Item
                                                        label="Tamanho"
                                                        name="resultTitleFontSize"
                                                    >
                                                        <InputNumber
                                                            min={10}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Row gutter={[16, 0]}>
                                                <Col span={12}>
                                                    <ColorField
                                                        label="Cor do texto"
                                                        name="resultTextColor"
                                                    />
                                                </Col>

                                                <Col span={12}>
                                                    <Form.Item
                                                        label="Tamanho do texto"
                                                        name="resultTextFontSize"
                                                    >
                                                        <InputNumber
                                                            min={10}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Form.Item
                                                label="Mostrar pontuação"
                                                name="resultShowScore"
                                                valuePropName="checked"
                                            >
                                                <Switch />
                                            </Form.Item>

                                            <Row gutter={[16, 0]}>
                                                <Col span={12}>
                                                    <ColorField
                                                        label="Cor da pontuação"
                                                        name="resultScoreColor"
                                                    />
                                                </Col>

                                                <Col span={12}>
                                                    <Form.Item
                                                        label="Tamanho da pontuação"
                                                        name="resultScoreFontSize"
                                                    >
                                                        <InputNumber
                                                            min={10}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Divider>
                                                Card
                                            </Divider>

                                            <Form.Item
                                                label="Fundo do card"
                                                name="resultCardBackground"
                                            >
                                                <Input />
                                            </Form.Item>

                                            <ColorField
                                                label="Cor da borda"
                                                name="resultCardBorderColor"
                                            />

                                            <Row gutter={[16, 0]}>
                                                <Col span={8}>
                                                    <Form.Item
                                                        label="Borda"
                                                        name="resultCardBorderWidth"
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={8}>
                                                    <Form.Item
                                                        label="Raio"
                                                        name="resultCardBorderRadius"
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={8}>
                                                    <Form.Item
                                                        label="Padding"
                                                        name="resultCardPadding"
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Row gutter={[16, 0]}>
                                                <Col span={12}>
                                                    <Form.Item
                                                        label="Largura"
                                                        name="resultCardWidth"
                                                    >
                                                        <InputNumber
                                                            min={200}
                                                            max={1080}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={12}>
                                                    <Form.Item
                                                        label="Posição"
                                                        name="resultCardPosition"
                                                    >
                                                        <Select
                                                            options={[
                                                                {
                                                                    value:
                                                                        'LEFT',
                                                                    label:
                                                                        'Esquerda',
                                                                },
                                                                {
                                                                    value:
                                                                        'CENTER',
                                                                    label:
                                                                        'Centro',
                                                                },
                                                                {
                                                                    value:
                                                                        'RIGHT',
                                                                    label:
                                                                        'Direita',
                                                                },
                                                                {
                                                                    value:
                                                                        'TOP',
                                                                    label:
                                                                        'Topo',
                                                                },
                                                                {
                                                                    value:
                                                                        'BOTTOM',
                                                                    label:
                                                                        'Rodapé',
                                                                },
                                                            ]}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Divider>
                                                Imagem do perfil
                                            </Divider>

                                            <Form.Item
                                                label="Mostrar imagem do perfil"
                                                name="resultShowImage"
                                                valuePropName="checked"
                                            >
                                                <Switch />
                                            </Form.Item>

                                            <Row gutter={[16, 0]}>
                                                <Col span={12}>
                                                    <Form.Item
                                                        label="Largura"
                                                        name="resultImageWidth"
                                                    >
                                                        <InputNumber
                                                            min={20}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={12}>
                                                    <Form.Item
                                                        label="Arredondamento"
                                                        name="resultImageBorderRadius"
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Divider>
                                                Botão
                                            </Divider>

                                            <Form.Item
                                                label="Texto"
                                                name="resultButtonText"
                                            >
                                                <Input />
                                            </Form.Item>

                                            <Row gutter={[16, 0]}>
                                                <Col span={12}>
                                                    <ColorField
                                                        label="Cor"
                                                        name="resultButtonColor"
                                                    />
                                                </Col>

                                                <Col span={12}>
                                                    <ColorField
                                                        label="Cor do texto"
                                                        name="resultButtonTextColor"
                                                    />
                                                </Col>
                                            </Row>

                                            <Row gutter={[16, 0]}>
                                                <Col span={6}>
                                                    <Form.Item
                                                        label="Largura"
                                                        name="resultButtonWidth"
                                                    >
                                                        <InputNumber
                                                            min={50}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={6}>
                                                    <Form.Item
                                                        label="Altura"
                                                        name="resultButtonHeight"
                                                    >
                                                        <InputNumber
                                                            min={20}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={6}>
                                                    <Form.Item
                                                        label="Raio"
                                                        name="resultButtonBorderRadius"
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={6}>
                                                    <Form.Item
                                                        label="Fonte"
                                                        name="resultButtonFontSize"
                                                    >
                                                        <InputNumber
                                                            min={10}
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        </>
                                    ),
                                },
                                // ==================================
                                // FORMULÁRIO
                                // ==================================

                                {
                                    key:
                                        'form',

                                    label:
                                        'Formulário',

                                    children: (
                                        <>
                                            <Alert
                                                type="info"
                                                showIcon
                                                message="Personalização do formulário de participação"
                                                description="Estas configurações alteram a tela de cadastro exibida antes ou depois do jogo."
                                                style={{
                                                    marginBottom:
                                                        20,
                                                }}
                                            />

                                            <Form.Item
                                                label="Página de formulário ativa"
                                                name="formPageEnabled"
                                                valuePropName="checked"
                                            >
                                                <Switch />
                                            </Form.Item>

                                            <Divider>
                                                Fundo
                                            </Divider>

                                            <ColorField
                                                label="Cor do fundo"
                                                name="formPageBackgroundColor"
                                            />

                                            <MediaField
                                                form={
                                                    form
                                                }
                                                label="Imagem de fundo"
                                                fieldName="formPageBackgroundUrl"
                                                type="image"
                                                uploading={
                                                    uploading
                                                }
                                                onUpload={
                                                    handleUpload
                                                }
                                            />

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col
                                                    xs={24}
                                                    md={8}
                                                >
                                                    <ColorField
                                                        label="Cor do overlay"
                                                        name="formPageOverlayColor"
                                                    />
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={8}
                                                >
                                                    <Form.Item
                                                        label="Opacidade do overlay"
                                                        name="formPageOverlayOpacity"
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            max={100}
                                                            step={5}
                                                            addonAfter="%"
                                                            style={{
                                                                width: '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={8}
                                                >
                                                    <Form.Item
                                                        label="Desfoque do fundo"
                                                        name="formPageBackgroundBlur"
                                                    >
                                                        <InputNumber
                                                            min={
                                                                0
                                                            }
                                                            max={
                                                                50
                                                            }
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Divider>
                                                Logo
                                            </Divider>

                                            <MediaField
                                                form={
                                                    form
                                                }
                                                label="Logo"
                                                fieldName="formPageLogoUrl"
                                                type="image"
                                                uploading={
                                                    uploading
                                                }
                                                onUpload={
                                                    handleUpload
                                                }
                                            />

                                            <Form.Item
                                                label="Largura da logo"
                                                name="formPageLogoSize"
                                            >
                                                <InputNumber
                                                    min={
                                                        20
                                                    }
                                                    max={
                                                        1080
                                                    }
                                                    addonAfter="px"
                                                    style={{
                                                        width:
                                                            '100%',
                                                    }}
                                                />
                                            </Form.Item>

                                            <Divider>
                                                Títulos
                                            </Divider>

                                            <Form.Item
                                                label="Título"
                                                name="formPageTitle"
                                            >
                                                <Input />
                                            </Form.Item>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <ColorField
                                                        label="Cor do título"
                                                        name="formPageTitleColor"
                                                    />
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <Form.Item
                                                        label="Tamanho do título"
                                                        name="formPageTitleFontSize"
                                                    >
                                                        <InputNumber
                                                            min={
                                                                10
                                                            }
                                                            max={
                                                                120
                                                            }
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Form.Item
                                                label="Subtítulo"
                                                name="formPageSubtitle"
                                            >
                                                <Input.TextArea
                                                    rows={
                                                        3
                                                    }
                                                />
                                            </Form.Item>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <ColorField
                                                        label="Cor do subtítulo"
                                                        name="formPageSubtitleColor"
                                                    />
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <Form.Item
                                                        label="Tamanho do subtítulo"
                                                        name="formPageSubtitleFontSize"
                                                    >
                                                        <InputNumber
                                                            min={
                                                                10
                                                            }
                                                            max={
                                                                80
                                                            }
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Divider>
                                                Card
                                            </Divider>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <ColorField
                                                        label="Cor do card"
                                                        name="formPageCardBackground"
                                                    />
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <Form.Item
                                                        label="Opacidade do card"
                                                        name="formPageCardOpacity"
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            max={100}
                                                            step={5}
                                                            addonAfter="%"
                                                            style={{
                                                                width: '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col
                                                    xs={24}
                                                    md={8}
                                                >
                                                    <ColorField
                                                        label="Cor da borda"
                                                        name="formPageCardBorderColor"
                                                    />
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={8}
                                                >
                                                    <Form.Item
                                                        label="Espessura"
                                                        name="formPageCardBorderWidth"
                                                    >
                                                        <InputNumber
                                                            min={
                                                                0
                                                            }
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={8}
                                                >
                                                    <Form.Item
                                                        label="Arredondamento"
                                                        name="formPageCardBorderRadius"
                                                    >
                                                        <InputNumber
                                                            min={
                                                                0
                                                            }
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Form.Item
                                                label="Usar sombra"
                                                name="formPageCardShadow"
                                                valuePropName="checked"
                                            >
                                                <Switch />
                                            </Form.Item>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col
                                                    xs={24}
                                                    md={8}
                                                >
                                                    <Form.Item
                                                        label="Largura do card"
                                                        name="formPageCardWidth"
                                                    >
                                                        <InputNumber
                                                            min={
                                                                200
                                                            }
                                                            max={
                                                                1920
                                                            }
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={8}
                                                >
                                                    <Form.Item
                                                        label="Padding"
                                                        name="formPageCardPadding"
                                                    >
                                                        <InputNumber
                                                            min={
                                                                0
                                                            }
                                                            max={
                                                                200
                                                            }
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={8}
                                                >
                                                    <Form.Item
                                                        label="Posição"
                                                        name="formPageCardPosition"
                                                    >
                                                        <Select
                                                            options={[
                                                                {
                                                                    value:
                                                                        'TOP',
                                                                    label:
                                                                        'Topo',
                                                                },
                                                                {
                                                                    value:
                                                                        'CENTER',
                                                                    label:
                                                                        'Centro',
                                                                },
                                                                {
                                                                    value:
                                                                        'BOTTOM',
                                                                    label:
                                                                        'Rodapé',
                                                                },
                                                            ]}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Divider>
                                                Campos
                                            </Divider>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <ColorField
                                                        label="Cor dos labels"
                                                        name="formPageLabelColor"
                                                    />
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <Form.Item
                                                        label="Tamanho dos labels"
                                                        name="formPageLabelFontSize"
                                                    >
                                                        <InputNumber
                                                            min={
                                                                8
                                                            }
                                                            max={
                                                                50
                                                            }
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col
                                                    xs={24}
                                                    md={8}
                                                >
                                                    <ColorField
                                                        label="Fundo do campo"
                                                        name="formPageInputBackground"
                                                    />
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={8}
                                                >
                                                    <ColorField
                                                        label="Texto do campo"
                                                        name="formPageInputTextColor"
                                                    />
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={8}
                                                >
                                                    <ColorField
                                                        label="Borda do campo"
                                                        name="formPageInputBorderColor"
                                                    />
                                                </Col>
                                            </Row>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col
                                                    xs={24}
                                                    md={4}
                                                >
                                                    <Form.Item
                                                        label="Raio"
                                                        name="formPageInputBorderRadius"
                                                    >
                                                        <InputNumber
                                                            min={
                                                                0
                                                            }
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={4}
                                                >
                                                    <Form.Item
                                                        label="Altura"
                                                        name="formPageInputHeight"
                                                    >
                                                        <InputNumber
                                                            min={
                                                                20
                                                            }
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={4}
                                                >
                                                    <Form.Item
                                                        label="Fonte"
                                                        name="formPageInputFontSize"
                                                    >
                                                        <InputNumber
                                                            min={
                                                                8
                                                            }
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <ColorField
                                                        label="Texto do consentimento"
                                                        name="formPageConsentTextColor"
                                                    />
                                                </Col>
                                            </Row>

                                            <Divider>
                                                Botão
                                            </Divider>

                                            <Form.Item
                                                label="Texto do botão"
                                                name="formPageButtonText"
                                            >
                                                <Input />
                                            </Form.Item>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <ColorField
                                                        label="Cor do botão"
                                                        name="formPageButtonColor"
                                                    />
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <ColorField
                                                        label="Cor do texto"
                                                        name="formPageButtonTextColor"
                                                    />
                                                </Col>
                                            </Row>

                                            <Row
                                                gutter={[
                                                    16,
                                                    0,
                                                ]}
                                            >
                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <Form.Item
                                                        label="Altura"
                                                        name="formPageButtonHeight"
                                                    >
                                                        <InputNumber
                                                            min={
                                                                20
                                                            }
                                                            max={
                                                                200
                                                            }
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>

                                                <Col
                                                    xs={24}
                                                    md={12}
                                                >
                                                    <Form.Item
                                                        label="Arredondamento"
                                                        name="formPageButtonBorderRadius"
                                                    >
                                                        <InputNumber
                                                            min={
                                                                0
                                                            }
                                                            max={
                                                                100
                                                            }
                                                            addonAfter="px"
                                                            style={{
                                                                width:
                                                                    '100%',
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        </>
                                    ),
                                },
                            ]}
                        />
                    </Card>
                </Col>

                {/* ==========================================
                    PREVIEW
                ========================================== */}

                <Col
                    xs={24}
                    xl={9}
                >
                    <div
                        style={{
                            position:
                                'sticky',

                            top:
                                20,
                        }}
                    >
                        <Card
                            title="Preview"
                        >
                            <Space
                                wrap
                                style={{
                                    marginBottom:
                                        16,
                                }}
                            >
                                <Button
                                    type={
                                        previewScreen ===
                                            'GENERAL'
                                            ? 'primary'
                                            : 'default'
                                    }
                                    onClick={() =>
                                        setPreviewScreen(
                                            'GENERAL',
                                        )
                                    }
                                >
                                    Geral
                                </Button>
                                <Button
                                    type={
                                        previewScreen ===
                                            'START'
                                            ? 'primary'
                                            : 'default'
                                    }
                                    onClick={() =>
                                        setPreviewScreen(
                                            'START',
                                        )
                                    }
                                >
                                    Inicial
                                </Button>

                                <Button
                                    type={
                                        previewScreen ===
                                            'QUESTION'
                                            ? 'primary'
                                            : 'default'
                                    }
                                    onClick={() =>
                                        setPreviewScreen(
                                            'QUESTION',
                                        )
                                    }
                                >
                                    Pergunta
                                </Button>

                                <Button
                                    type={
                                        previewScreen ===
                                            'RESULT'
                                            ? 'primary'
                                            : 'default'
                                    }
                                    onClick={() =>
                                        setPreviewScreen(
                                            'RESULT',
                                        )
                                    }
                                >
                                    Resultado
                                </Button>
                                <Button
                                    type={
                                        previewScreen ===
                                            'FORM'
                                            ? 'primary'
                                            : 'default'
                                    }
                                    onClick={() =>
                                        setPreviewScreen(
                                            'FORM',
                                        )
                                    }
                                >
                                    Formulário
                                </Button>
                            </Space>

                            <div
                                style={{
                                    display:
                                        'flex',

                                    justifyContent:
                                        'center',
                                }}
                            >
                                <QuizPreview
                                    values={
                                        values
                                    }
                                    previewScreen={
                                        previewScreen
                                    }
                                />
                            </div>

                            <div
                                style={{
                                    textAlign:
                                        'center',

                                    marginTop:
                                        12,
                                }}
                            >
                                <Text
                                    type="secondary"
                                >
                                    {`Proporção ${Number(
                                        values.screenWidth,
                                    ) ||
                                        (
                                            values.orientation ===
                                                'HORIZONTAL'
                                                ? 1920
                                                : 1080
                                        )
                                        } x ${Number(
                                            values.screenHeight,
                                        ) ||
                                        (
                                            values.orientation ===
                                                'HORIZONTAL'
                                                ? 1080
                                                : 1920
                                        )
                                        }`}
                                </Text>
                            </div>
                        </Card>
                    </div>
                </Col>
            </Row>
        </Form>
    )
}

export default QuizAppearance



